import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import io, { Socket } from "socket.io-client";
import { QRCodeSVG } from "qrcode.react";
import moment from "moment";
import Cookies from "js-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faCog,
  faCheck,
  faKeyboard,
  faUser,
  faTimes,
  faDoorOpen,
  faShieldAlt,
  faRecordVinyl,
  faUsers,
  faVolumeUp,
  faMicrophone,
  faPlayCircle,
  faSignInAlt,
  faClock,
  faCalendar,
  faClipboardCheck,
  faInfoCircle,
  faEye,
  faEyeSlash,
  faSun,
  faMoon,
  faChevronDown,
  faChevronUp,
  faQuestionCircle,
  faLanguage,
  faWandMagicSparkles,
  faBolt,
  faLock,
  faLightbulb,
  faDownload,
  faArrowRight,
  faArrowLeft,
  faBrain,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import {
  faWindows,
  faApple,
  faAndroid,
  faLinux,
} from "@fortawesome/free-brands-svg-icons";
import {
  PremiumButton,
} from "../components_modern/core";
import { ModernTooltip } from "../components_modern/core/widgets/ModernTooltip";
import { MediasfuColors, MediasfuSpacing, MediasfuBorders, MediasfuTypography } from "../components_modern/core";
import { applyMediasfuBrandPalette, MEDIASFU_BRAND, brandSurface } from "./brandPalette";
import { AuroraBackdrop, MicLevelMeter, OrbitPlaceholder, SetupVisualStyles } from "./setupVisuals";

applyMediasfuBrandPalette();
// Logo is served from public folder
import { VirtualBackgroundSelector, useVirtualBackground } from "./VirtualBackground";
import {
  compositeVirtualBackgroundFrame,
  DEFAULT_BACKGROUND_BLUR_PIXELS,
  isVirtualBackgroundBlur,
  VIRTUAL_BACKGROUND_BLUR,
} from "../methods/utils/virtualBackgroundCompositor";

export interface ModernMeetingSetupOptions {
  data?: {
    apiKey?: string;
    apiUserName?: string;
    subusername?: string;
    eventType?: string;
    url?: string;
  };
  eventType?: "chat" | "broadcast" | "webinar" | "conference";
  isDarkMode?: boolean;
  useFixedLink?: boolean;
  allowScan?: boolean;
  /** Show the MediaSFU native-app promotion beneath the meeting setup. */
  showDownloadPromo?: boolean;
  /** Download hub used by the native-app promotion. */
  downloadPageUrl?: string;
  onJoin?: (params: {
    meetingID: string;
    userName: string;
    secureCode: string;
    url: string;
    secret: string;
    audioPreference?: string;
    videoPreference?: string;
    audioOutputPreference?: string;
    virtualBackground?: any;
    isDarkMode?: boolean;
    eventType?: any;
    selfieSegmentation?: any;
  }) => void;
}

type RecordingParams = {
  recordingAudioPausesLimit: number;
  recordingAudioSupport: boolean;
  recordingAudioPeopleLimit: number;
  recordingAudioParticipantsTimeLimit: number;
  recordingVideoPausesLimit: number;
  recordingVideoSupport: boolean;
  recordingVideoPeopleLimit: number;
  recordingVideoParticipantsTimeLimit: number;
  recordingAllParticipantsSupport: boolean;
  recordingVideoParticipantsSupport: boolean;
  recordingAllParticipantsFullRoomSupport: boolean;
  recordingVideoParticipantsFullRoomSupport: boolean;
  recordingPreferredOrientation: "landscape" | "portrait";
  recordingSupportForOtherOrientation: boolean;
  recordingMultiFormatsSupport: boolean;
  recordingHLSSupport: boolean;
};

type MeetingParams = {
  itemPageLimit: number;
  mediaType: "video" | "audio";
  addCoHost: boolean;
  targetOrientation: "landscape" | "portrait" | "neutral";
  targetOrientationHost: "landscape" | "portrait" | "neutral";
  targetResolution: "qhd" | "fhd" | "hd" | "sd" | "QnHD";
  targetResolutionHost: "qhd" | "fhd" | "hd" | "sd" | "QnHD";
  type: "chat" | "broadcast" | "webinar" | "conference";
  audioSetting: "approval" | "disallow" | "allow";
  backendAudioDenoise?: { enabled: boolean; profile?: "arnndn" };
  videoSetting: "approval" | "disallow" | "allow";
  screenshareSetting: "approval" | "disallow" | "allow";
  chatSetting: "disallow" | "allow";
  supportSIP: boolean;
  directionSIP: "inbound" | "outbound" | "both";
  videoDeviceId?: string;
  // Audio codec preferences
  preferPCMA?: boolean;
  // Translation Support
  supportTranslation?: boolean;
  translationConfigNickName?: string;
  // Room Capacity Features
  supportFlexRoom?: boolean; // 2x capacity with 25% producer limit
  supportMaxRoom?: boolean; // 4x capacity with 5% producer limit
  // Webhook
  webhookUrl?: string | null;
  webhookSecret?: string | null;
};

type SocketRef = React.MutableRefObject<Socket | null>;

// Set to true to always use stagerooms, false for environment-specific URLs
const USE_DEMO_MODE = process.env.REACT_APP_USE_DEMO_MODE === 'true';
// Helper to get the correct socket URL based on meeting ID prefix
const getSocketUrl = (meetingIdPrefix: string, useFixedLink?: boolean): string => {
  
  const STAGEROOMS_URL = "https://stagerooms.mediasfu.com/media";
  useFixedLink = USE_DEMO_MODE;

  
  if (USE_DEMO_MODE || useFixedLink) {
    return STAGEROOMS_URL;
  }
  
  switch (meetingIdPrefix.toLowerCase()) {
    case 'd':
      return "https://demos.mediasfu.com/media";
    case 's':
      return "https://sandbox.mediasfu.com/media";
    case 'p':
      return "https://meeting.mediasfu.com/media";
    default:
      return STAGEROOMS_URL;
  }
};

type DownloadPlatform = {
  key: "ios" | "android" | "windows" | "macos" | "linux" | null;
  label: string;
};

const detectDownloadPlatform = (): DownloadPlatform => {
  if (typeof navigator === "undefined") {
    return { key: null, label: "View app downloads" };
  }

  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const platform = `${nav.userAgentData?.platform || ""} ${nav.platform || ""} ${nav.userAgent || ""}`.toLowerCase();

  if (/android/.test(platform)) return { key: "android", label: "Get MediaSFU for Android" };
  if (/iphone|ipad|ipod/.test(platform)) return { key: "ios", label: "Get MediaSFU for iOS" };
  if (/mac/.test(platform) && nav.maxTouchPoints > 1) return { key: "ios", label: "Get MediaSFU for iOS" };
  if (/win/.test(platform)) return { key: "windows", label: "Get MediaSFU for Windows" };
  if (/mac/.test(platform)) return { key: "macos", label: "Get MediaSFU for macOS" };
  if (/linux|x11/.test(platform)) return { key: "linux", label: "Get MediaSFU for Linux" };

  return { key: null, label: "View app downloads" };
};

const ModernMeetingSetup: React.FC<ModernMeetingSetupOptions> = ({
  data,
  eventType,
  isDarkMode: initialIsDarkMode = false,
  useFixedLink,
  allowScan = false,
  showDownloadPromo = true,
  downloadPageUrl = "https://mediasfu.com/download",
  onJoin,
}) => {
  const socket: SocketRef = useRef<Socket | null>(null);
  const defaultURL = useRef(getSocketUrl('d', useFixedLink));

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("isDarkMode");
    if (saved !== null) {
      return saved === "true";
    }
    return initialIsDarkMode;
  });

  useEffect(() => {
    localStorage.setItem("isDarkMode", String(isDarkMode));
  }, [isDarkMode]);
  const [showTips, setShowTips] = useState(true);
  const downloadPlatform = useMemo(detectDownloadPlatform, []);
  // The CTA wears the detected platform's own logo; the generic download glyph
  // is the fallback when we could not identify one.
  const downloadPlatformIcon = useMemo(() => {
    switch (downloadPlatform.key) {
      case "ios":
      case "macos":
        return faApple;
      case "android":
        return faAndroid;
      case "windows":
        return faWindows;
      case "linux":
        return faLinux;
      default:
        return faDownload;
    }
  }, [downloadPlatform.key]);
  const otherPlatforms = useMemo(() => {
    const all = [
      { icon: faApple, label: "iOS and macOS", keys: ["ios", "macos"] },
      { icon: faAndroid, label: "Android", keys: ["android"] },
      { icon: faWindows, label: "Windows", keys: ["windows"] },
      { icon: faLinux, label: "Linux", keys: ["linux"] },
      { icon: faGlobe, label: "Web", keys: [] as string[] },
    ];
    return all.filter((p) => !downloadPlatform.key || !p.keys.includes(downloadPlatform.key));
  }, [downloadPlatform.key]);
  const downloadPromoHref = useMemo(() => {
    const params = new URLSearchParams({
      utm_source: "meeting_setup",
      utm_medium: "web",
      utm_campaign: "app_download",
    });
    if (downloadPlatform.key) params.set("platform", downloadPlatform.key);
    return `${downloadPageUrl}${downloadPageUrl.includes("?") ? "&" : "?"}${params.toString()}`;
  }, [downloadPageUrl, downloadPlatform.key]);
  const allDownloadsHref = useMemo(() => {
    const params = new URLSearchParams({
      utm_source: "meeting_setup",
      utm_medium: "web",
      utm_campaign: "app_download",
    });
    return `${downloadPageUrl}${downloadPageUrl.includes("?") ? "&" : "?"}${params.toString()}`;
  }, [downloadPageUrl]);
  const [quickSetup, setQuickSetup] = useState(true); // Quick setup mode - show only essential fields
  const [, setForceUpdate] = useState(0);
  const forceRender = () => setForceUpdate((prev) => prev + 1);

  const [meetingID, setMeetingID] = useState("");
  const [userName, setUserName] = useState("");
  const [capacity, setCapacity] = useState<number | "">(2);
  const [secureCode, setSecureCode] = useState("pass");
  const [existingNames, setExistingNames] = useState<string[]>([]);
  const [existingBans, setExistingBans] = useState<string[]>([]);
  const [adminStarted, setAdminStarted] = useState(false);
  const [waitingForAdmin, setWaitingForAdmin] = useState(false);
  const [waitRoom, setWaitRoom] = useState(false);
  const [checkForAdmin, setCheckForAdmin] = useState(false);
  const [pender, setPender] = useState(false);
  const [deferAlertForCapacity, setDeferAlertForCapacity] = useState(false);
  const [userSecret, setUserSecret] = useState("");
  const [urll, setUrll] = useState("");
  const selectedImage = useRef<string | null>(null);
  const localStreamVideo = useRef<MediaStream | null>(null);
  const videoAlreadyOn = useRef(false);
  const videoPreference = useRef<string | null>(null);
  const audioPreference = useRef<string | null>(null);
  const audioOutputPreference = useRef<string | null>(null);
  const currentFacingMode = useRef<string>("user");
  // State to trigger re-renders when device selection changes
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState("");
  const [nameWarn, setNameWarn] = useState("");
  const [codeVisible, setCodeVisible] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiUserName, setApiUserName] = useState<string | null>(null);
  const [subusername, setSubUserName] = useState<string | null>(null);
  const [actType, setActType] = useState<MeetingParams["type"]>(() => {
    const saved = localStorage.getItem("eventType");
    if (saved) {
      return saved as MeetingParams["type"];
    }
    return eventType || "conference";
  });

  useEffect(() => {
    localStorage.setItem("eventType", actType);
  }, [actType]);
  const [itemPageLimit, setItemPageLimit] = useState(2);

  useEffect(() => {
    if (actType === "broadcast") {
      if (itemPageLimit !== 1) setItemPageLimit(1);
    } else if (actType === "chat") {
      if (itemPageLimit !== 2) setItemPageLimit(2);
    } else if (actType === "webinar" || actType === "conference") {
      if (typeof capacity === "number" && itemPageLimit > capacity) {
        setItemPageLimit(capacity);
      }
    }
  }, [actType, capacity, itemPageLimit]);

  const [refRoomCapacity, setRefRoomCapacity] = useState(8);
  const capacityLimits = useRef<Record<string, number> | null>(null);
  const [safeRoom, setSafeRoom] = useState(false);
  // Translation Support
  // const [supportTranslation, setSupportTranslation] = useState(false);
  // const [translationConfigNickName, setTranslationConfigNickName] = useState('');
  // // Room Capacity Features
  // const [supportFlexRoom, setSupportFlexRoom] = useState(false); // 2x capacity with 25% producer limit
  // const [supportMaxRoom, setSupportMaxRoom] = useState(false); // 4x capacity with 5% producer limit
  // // Audio codec preferences
  // const [preferPCMA, setPreferPCMA] = useState(false);
  // URL meeting ID mode (from /meeting/xxx route)
  const [urlMeetingMode, setUrlMeetingMode] = useState(false);
  const [urlMeetingValidated, setUrlMeetingValidated] = useState(false);
  const [selectedDurationMinutes, setSelectedDurationMinutes] = useState(15);
  const [alertDemo, setAlertDemo] = useState(false);
  const [maxMeetingDuration, setMaxMeetingDuration] = useState(0.25);
  const [apiType, setApiType] = useState<"demo" | "sandbox" | "production">(
    "demo" 
  ); 
  const durationOptions = useMemo(
    () =>
      generateIncrements(maxMeetingDuration).map((hours) => ({
        label: hours >= 1 ? `${hours} hrs` : `${Math.round(hours * 60)} mins`,
        value: Math.round(hours * 60),
      })),
    [maxMeetingDuration]
  );

  const waitingRoomDisabled = useMemo(
    () => actType === "chat" || apiType === "demo",
    [actType, apiType]
  );
  const safeRoomDisabled = useMemo(
    () => actType === "chat" || apiType === "demo" || apiType === "sandbox",
    [actType, apiType]
  );

  const recordingParams = useRef<RecordingParams>({
    recordingAudioPausesLimit: 0,
    recordingAudioSupport: false,
    recordingAudioPeopleLimit: 0,
    recordingAudioParticipantsTimeLimit: 0,
    recordingVideoPausesLimit: 0,
    recordingVideoSupport: false,
    recordingVideoPeopleLimit: 0,
    recordingVideoParticipantsTimeLimit: 0,
    recordingAllParticipantsSupport: false,
    recordingVideoParticipantsSupport: false,
    recordingAllParticipantsFullRoomSupport: false,
    recordingVideoParticipantsFullRoomSupport: false,
    recordingPreferredOrientation: "landscape",
    recordingSupportForOtherOrientation: false,
    recordingMultiFormatsSupport: false,
    recordingHLSSupport: true,
  });

  const meetingParams = useRef<MeetingParams>({
    itemPageLimit: 2,
    mediaType: "video",
    addCoHost: true,
    targetOrientation: "neutral",
    targetOrientationHost: "neutral",
    targetResolution: "sd",
    targetResolutionHost: "sd",
    type: eventType || "conference",
    audioSetting: "allow",
    videoSetting: "allow",
    screenshareSetting: "allow",
    chatSetting: "allow",
    supportSIP: false,
    directionSIP: "both",
    // Translation Support - defaults to false for demo mode
    supportTranslation: false,
    translationConfigNickName: '',
    // Room Capacity Features - defaults to false for demo mode
    supportFlexRoom: false,
    supportMaxRoom: false,
    // Audio codec preferences
    preferPCMA: false,
  });

  const refMeetingParams = useRef<MeetingParams>({ ...meetingParams.current });
  const refRecordingParams = useRef<RecordingParams>({ ...recordingParams.current });
  const settingsRef = useRef<HTMLDivElement>(null);

  const [passWord, setPassWord] = useState("");
  const [adminNamed, setAdminNamed] = useState("");
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "danger" | "warning" | "info">("success");
  const [loading, setLoading] = useState(false);
  const [waitingModalVisible, setWaitingModalVisible] = useState(false);
  const [adminWelcomeModalVisible, setAdminWelcomeModalVisible] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState("");
  const [showTranslationBanner, setShowTranslationBanner] = useState(() => {
    // Check localStorage to see if user dismissed it before
    if (typeof window !== 'undefined') {
      return localStorage.getItem('mediasfu_translation_banner_dismissed') !== 'true';
    }
    return true;
  });

  const [showRecording, setShowRecording] = useState(false);
  const [expandRecordingParams, setExpandRecordingParams] = useState(false);
  const [expandMeetingParams, setExpandMeetingParams] = useState(false);
  const [expandAdvancedSettings, setExpandAdvancedSettings] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_showStartMeetingPanel, setShowStartMeetingPanel] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_showMediaSettings, setShowMediaSettings] = useState(false);
  const [initMedia, setInitMedia] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);

  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [microphoneDevices, setMicrophoneDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [virtualBackground, setVirtualBackground] = useState<string | null>(null);

  const videoOutputPreviewRef = useRef<HTMLVideoElement | null>(null);
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);
  const audioOutputPreviewRef = useRef<HTMLAudioElement | null>(null);
  const mainCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureVideoRef = useRef<HTMLVideoElement | null>(null);
  const backgroundCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const clonedTrack = useRef<MediaStreamTrack | null>(null);
  const clonedStream = useRef<MediaStream | null>(null);
  const segmentVideo = useRef<MediaStream | null>(null);
  const keepSegmentationRunning = useRef(false);
  const animationFrameId = useRef<number | null>(null);
  const frameRate = 10;

  const userNameInputMainRef = useRef<HTMLInputElement | null>(null);
  const selectedTime = useRef<string | null>(null);
  const eventScheduled = useRef(false);
  const scheduledDate = useRef<number>(0);
  const finalDataRef = useRef(data || null);
  const dataChanged = useRef(false);

  useEffect(() => {
    if (!durationOptions.length) return;
    if (!durationOptions.some((o) => o.value === selectedDurationMinutes)) {
      setSelectedDurationMinutes(durationOptions[0].value);
    }
  }, [durationOptions, selectedDurationMinutes]);

  // When actType changes, update refRoomCapacity & initial capacity from capacityLimits
  useEffect(() => {
    if (capacityLimits.current) {
      const limit = capacityLimits.current[actType] ?? capacityLimits.current['other'] ?? 8;
      const maxCap = actType === 'chat' ? 2 : limit;
      setRefRoomCapacity(maxCap);
      if (quickSetup) {
        const initCap = actType === 'chat' ? 2 : Math.max(2, Math.ceil(maxCap / 2));
        setCapacity(initCap);
      } else {
        // Clamp existing capacity to new max
        if (typeof capacity === 'number' && capacity > maxCap) {
          setCapacity(maxCap);
        }
      }
    }
  }, [actType]);

  useEffect(() => {
    if (waitingRoomDisabled && waitRoom) setWaitRoom(false);
  }, [waitingRoomDisabled, waitRoom]);

  useEffect(() => {
    if (safeRoomDisabled && safeRoom) setSafeRoom(false);
  }, [safeRoomDisabled, safeRoom]);

  const { selfieSegmentation, isModelLoading, preloadModel, pauseSegmentation, processedStream } = useVirtualBackground();

  const resetProcess = useCallback(() => {
    setMeetingID("");
    setUserName("");
    setExistingNames([]);
    setExistingBans([]);
    setAdminStarted(false);
    setWaitingForAdmin(false);
    setWaitRoom(false);
    setUserSecret("");
    setUrll("");
    setWarningMessage("");
    setCodeVisible(false);
    setCurrentStep(1);
  }, []);

  const showAlert = useCallback(
    (message: string, type: "success" | "danger" | "warning" | "info" = "success", duration = 4000) => {
      setAlertMessage(message);
      setAlertType(type);
      setAlertVisible(true);
      if (duration > 0) {
        setTimeout(() => setAlertVisible(false), duration);
      }
    },
    []
  );

  const editMeetingID = (value: string) => {
    const modified = value.replace(/[^\w\s]/g, "").replace(/\s/g, "");
    return modified;
  };

  const editText = (value: string) => {
    const modified = value.replace(/[^\w\s]/g, "").replace(/\s/g, "");
    const firstChar = modified.charAt(0);
    const cleaned = /^[A-Za-z]/.test(firstChar) ? modified : "";
    if (cleaned.length > 10) {
      showAlert("Display name cannot exceed 10 characters. Trimmed to max.", "warning");
      return cleaned.slice(0, 10);
    }
    return cleaned;
  };

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift();
    return undefined;
  };

  const setCookie = (name: string, value: string, days: number) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();
    const domain =
      window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("127.0.0.1")
        ? ""
        : ";domain=.mediasfu.com";
    document.cookie = `${name}=${value};${expires};path=/${domain}`;
  };

  const isValidDomain = (domain: string, protocol: string) => {
    if (domain.includes("localhost") || domain.includes("127.0.0.1")) return false;
    if (protocol === "http:") return false;
    return true;
  };

  const generateRandomSubstring = (length: number) =>
    Math.random().toString(36).substring(2, 2 + length);

  const generateThreeRandomSubstrings = (length: number) => {
    const arr: string[] = [];
    for (let i = 0; i < 3; i++) arr.push(generateRandomSubstring(length));
    return arr;
  };

  const generateMeetingID = () => {
    const now = new Date();
    const randomDigits = Math.floor(10 + Math.random() * 99);
    const randPart = generateThreeRandomSubstrings(5);
    let meetingID_ =
      now.getTime().toString(36) +
      now.getUTCMilliseconds() +
      randomDigits.toString() +
      randPart[0][0] +
      randPart[1][2];
    meetingID_ = `${apiType.charAt(0)}${meetingID_}`;
    setMeetingID(meetingID_);
    return meetingID_;
  };

  const isReservedKeyword = (keyword: string) => {
    const jsReserved = [
      "break",
      "case",
      "catch",
      "class",
      "const",
      "continue",
      "debugger",
      "default",
      "delete",
      "do",
      "else",
      "export",
      "extends",
      "finally",
      "for",
      "function",
      "if",
      "import",
      "in",
      "instanceof",
      "new",
      "return",
      "super",
      "switch",
      "this",
      "throw",
      "try",
      "typeof",
      "var",
      "void",
      "while",
      "with",
      "yield",
      "enum",
      "implements",
      "interface",
      "let",
      "package",
      "private",
      "protected",
      "public",
      "static",
      "await",
      "abstract",
      "boolean",
      "byte",
      "char",
      "double",
      "final",
      "float",
      "goto",
      "int",
      "long",
      "native",
      "short",
      "synchronized",
      "throws",
      "transient",
      "volatile",
      "true",
      "false",
      "null",
      "undefined",
      "NaN",
      "Infinity",
    ];
    const bashReserved = [
      "if",
      "then",
      "else",
      "elif",
      "fi",
      "case",
      "esac",
      "for",
      "while",
      "do",
      "done",
      "in",
      "function",
      "time",
      "return",
      "exit",
      "shift",
      "export",
      "readonly",
      "alias",
      "eval",
      "unset",
      "local",
      "declare",
      "typeset",
      "readonly",
      "trap",
      "wait",
      "until",
      "select",
      "continue",
      "break",
      "test",
      "[[",
      "]]",
      "source",
      "true",
      "false",
      "null",
    ];
    return [...jsReserved, ...bashReserved].includes(keyword.toLowerCase());
  };

  function generateIncrements(maxValue: number) {
    const increments = [0.25];
    let current = 0.25;
    while (current < maxValue) {
      if (current < 3) current += 0.25;
      else if (current < 6) current += 0.5;
      else if (current < 12) current += 1;
      else if (current < 24) current += 3;
      if (current <= maxValue) increments.push(current);
    }
    return increments;
  }

  const validateMeetingID = (id: string) => {
    if (!id || typeof id !== "string") return false;
    if (id.length < 7) return false;
    if (!/^[a-zA-Z0-9]+$/.test(id)) return false;
    return true;
  };

  const createLoadingModal = () => setLoading(true);
  const hideLoadingModal = () => setLoading(false);

  const populateDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        throw new Error("Media devices not supported");
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter((d) => d.kind === "videoinput");
      const audios = devices.filter((d) => d.kind === "audioinput");
      const outputs = devices.filter((d) => d.kind === "audiooutput");
      setCameraDevices(videos);
      setMicrophoneDevices(audios);
      setSpeakerDevices(outputs);

      const savedAudio = getCookie("audioDevice");
      if (savedAudio && audios.some((d) => d.deviceId === savedAudio)) {
        audioPreference.current = savedAudio;
        setSelectedAudioDevice(savedAudio);
      }
      const savedVideo = getCookie("videoDevice");
      if (savedVideo && videos.some((d) => d.deviceId === savedVideo)) {
        videoPreference.current = savedVideo;
        setSelectedVideoDevice(savedVideo);
      }
      const savedFacing = getCookie("facingMode");
      if (savedFacing) {
        currentFacingMode.current = savedFacing;
      }
      const savedOut = getCookie("audioOutputDevice");
      if (savedOut && outputs.some((d) => d.deviceId === savedOut)) {
        audioOutputPreference.current = savedOut;
      }

      // Auto-apply background if saved - trigger camera if VB is needed
      const userUseBackground = getCookie("useBackground");
      const userbackgroundSrc = getCookie("backgroundSrc");
      if (userUseBackground === "true") {
        const bgSrc = userbackgroundSrc || "https://mediasfu.com/images/backgrounds/wall.jpg";
        selectedImage.current = bgSrc;
        setVirtualBackground(bgSrc);
        // Auto-trigger camera since VB requires it - will be applied in useEffect
        // Camera start is deferred to avoid blocking - actual VB applied via useEffect
      }
    } catch (err) {
      console.error("populateDevices", err);
      showAlert("Failed to access media devices. Please check permissions.", "danger");
    }
  }, [showAlert]);

  const streamSuccessNull = useCallback(async () => {
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: videoPreference.current || undefined,
          facingMode: currentFacingMode.current || 'user',
          width: { ideal: 480 },
          height: { ideal: 480 },
        },
        audio: {
          deviceId: audioPreference.current || undefined,
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const videoEl = videoOutputPreviewRef.current;
      const audioEl = audioOutputPreviewRef.current;
      if (videoEl) videoEl.srcObject = stream;
      if (audioEl) audioEl.srcObject = stream;
      localStreamVideo.current = stream;
      videoAlreadyOn.current = true;
    } catch (error: any) {
      console.error("streamSuccessNull", error);
      videoAlreadyOn.current = false;
      if (error.name === "NotAllowedError") {
        showAlert("Camera/microphone access denied. Please allow permissions.", "danger");
      } else if (error.name === "NotFoundError") {
        showAlert("No camera or microphone found. Please connect a device.", "danger");
      } else {
        showAlert("Failed to access media devices. Please check your setup.", "danger");
      }
    }
  }, [showAlert]);

  const previewMedia = useCallback(async () => {
    try {
      setAlertVisible(false);
      await populateDevices();
    } catch (error) {
      console.error("previewMedia", error);
      showAlert("Failed to access media devices. Please check permissions.", "danger");
    }
  }, [populateDevices, showAlert]);

  const handleCameraChange = async (deviceId: string) => {
    videoPreference.current = deviceId || null;
    setSelectedVideoDevice(deviceId || "");
    try {
      if (localStreamVideo.current) {
        localStreamVideo.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: deviceId ? { exact: deviceId } : undefined, facingMode: currentFacingMode.current || 'user', width: { ideal: 480 }, height: { ideal: 480 } },
        audio: { deviceId: audioPreference.current || undefined },
      });
      const videoEl = videoOutputPreviewRef.current;
      if (videoEl) {
        videoEl.srcObject = stream;
        localStreamVideo.current = stream;
        videoAlreadyOn.current = true;
        try {
          const facingMode = stream.getVideoTracks()[0]?.getSettings().facingMode;
          if (facingMode) {
            setCookie("facingMode", facingMode, 30);
            currentFacingMode.current = facingMode;
          }
          if (deviceId) setCookie("videoDevice", deviceId, 30);
        } catch (err) {
          console.warn("Camera cookie", err);
        }
      }
    } catch (err) {
      console.error("handleCameraChange", err);
      videoAlreadyOn.current = false;
      showAlert("Camera access failed. Please check permissions.", "danger");
    }
  };

  const handleMicrophoneChange = async (deviceId: string) => {
    audioPreference.current = deviceId || null;
    setSelectedAudioDevice(deviceId || "");
    try {
      if (localStreamVideo.current) {
        localStreamVideo.current.getAudioTracks().forEach((t) => t.stop());
      }
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: videoPreference.current || undefined,
          facingMode: currentFacingMode.current || 'user',
          width: { ideal: 480 },
          height: { ideal: 480 },
        },
        audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const audioEl = audioOutputPreviewRef.current;
      if (audioEl) {
        audioEl.srcObject = stream;
        setCookie("audioDevice", deviceId, 30);
        localStreamVideo.current = stream;
      }
    } catch (error: any) {
      console.error("handleMicrophoneChange", error);
      if (error.name === "NotAllowedError") {
        showAlert("Microphone access denied.", "danger");
      } else if (error.name === "NotFoundError") {
        showAlert("Selected microphone not found.", "danger");
      } else if (error.name === "NotReadableError") {
        showAlert("Microphone is in use by another application.", "danger");
      } else {
        showAlert("Failed to switch microphone. Please try again.", "danger");
      }
    }
  };

  const handleAudioOutputChange = async (deviceId: string) => {
    audioOutputPreference.current = deviceId || null;
    try {
      const audioEl = audioOutputPreviewRef.current;
      if (!audioEl) throw new Error("audio element missing");
      if (!deviceId) {
        audioEl.pause();
        audioEl.srcObject = null;
        return;
      }
      if (!(audioEl as any).setSinkId) {
        showAlert("Audio output selection not supported in this browser.", "warning");
        return;
      }
      await (audioEl as any).setSinkId(deviceId);
      setCookie("audioOutputDevice", deviceId, 30);
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
        });
        audioEl.srcObject = testStream;
        const playPromise = audioEl.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            showAlert("Click the audio element to test the speaker.", "info");
          });
        }
      } catch (streamError) {
        console.warn("Audio output test stream", streamError);
      }
    } catch (error: any) {
      console.error("handleAudioOutputChange", error);
      if (error.name === "NotAllowedError") showAlert("Audio output access denied.", "danger");
      else if (error.name === "NotFoundError") showAlert("Selected audio output device not found.", "danger");
      else if (error.name === "AbortError") showAlert("Audio output change was interrupted.", "warning");
      else showAlert("Failed to change audio output. Please try again.", "danger");
    }
  };

  const loadImageToCanvas = (src: string | null) => {
    const canvas = mainCanvasRef.current;
    if (!canvas || !src) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      selectedImage.current = src;
      selfieSegmentationPreview(true);
    };
    img.src = src;
  };

  const handleImageUpload = (file?: File | null) => {
    if (!file) return;
    if (file.size > 2048 * 1024) {
      showAlert("File size must be less than 2MB.", "danger");
      return;
    }
    const validMimeTypes = ["image/jpeg", "image/png"];
    if (!validMimeTypes.includes(file.type)) {
      showAlert("Invalid file type. Only JPEG and PNG are allowed.", "danger");
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (img.width !== 1920 || img.height !== 1920) {
        showAlert("Image dimensions must be 1920x1920.", "danger");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        selectedImage.current = result;
        loadImageToCanvas(result);
      };
      reader.readAsDataURL(file);
    };
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = (e.target?.result as string) || "";
    };
    reader.readAsDataURL(file);
    showAlert("Custom images need to be uploaded again when you join the meeting.", "success");
  };

  const handleVirtualBackgroundSelect = async (background: string | null) => {
    if (!background) {
      setVirtualBackground(null);
      selectedImage.current = null;
      localStorage.removeItem("virtualBackground");
      selfieSegmentationPreview(false);
      return;
    }
    
    // If camera is not on yet, turn it on first before applying the background
    if (!videoAlreadyOn.current) {
      try {
        // Start the camera first
        await streamSuccessNull();
        // Give it a moment to initialize
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (err) {
        console.error("Failed to start camera for virtual background", err);
        showAlert("Please turn on your camera first to use virtual backgrounds.", "warning");
        return;
      }
    }
    
    // Preload the segmentation model if not already loaded
    if (!selfieSegmentation.current) {
      await preloadModel();
    }
    
    if (background === "blur") {
      setVirtualBackground(VIRTUAL_BACKGROUND_BLUR);
      selectedImage.current = VIRTUAL_BACKGROUND_BLUR;
      localStorage.setItem("virtualBackground", VIRTUAL_BACKGROUND_BLUR);
      selfieSegmentationPreview(true);
    } else {
      setVirtualBackground(background);
      selectedImage.current = background;
      localStorage.setItem("virtualBackground", background);
      loadImageToCanvas(background);
    }
  };

  useEffect(() => {
    // Check localStorage first, then cookies for saved background
    let savedBackground = localStorage.getItem("virtualBackground");
    
    if (!savedBackground) {
      const userUseBackground = getCookie("useBackground");
      if (userUseBackground === "true") {
        savedBackground = getCookie("backgroundSrc") || "https://mediasfu.com/images/backgrounds/wall.jpg";
      }
    }

    if (savedBackground) {
      setVirtualBackground(savedBackground);
      selectedImage.current = savedBackground;
      
      // Wait for video AND model to be ready before applying
      const applyBackgroundWhenReady = async () => {
        let attempts = 0;
        const maxAttempts = 40; // 40 * 250ms = 10 seconds max
        let cameraStartAttempted = false;
        
        const checkAndApply = async () => {
          attempts++;
          
          // Check if video is ready
          const videoReady = videoAlreadyOn.current && 
            captureVideoRef.current && 
            localStreamVideo.current?.getVideoTracks()[0]?.readyState === "live";
          
          if (!videoReady) {
            // Try to start camera on first few attempts if it's not on
            if (!cameraStartAttempted && !videoAlreadyOn.current && attempts <= 3) {
              cameraStartAttempted = true;
              try {
                // Get media stream to start camera
                const constraints: MediaStreamConstraints = {
                  video: {
                    deviceId: videoPreference.current || undefined,
                    facingMode: currentFacingMode.current || 'user',
                    width: { ideal: 480 },
                    height: { ideal: 480 },
                  },
                  audio: {
                    deviceId: audioPreference.current || undefined,
                  },
                };
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                const videoEl = videoOutputPreviewRef.current;
                const audioEl = audioOutputPreviewRef.current;
                if (videoEl) videoEl.srcObject = stream;
                if (audioEl) audioEl.srcObject = stream;
                localStreamVideo.current = stream;
                videoAlreadyOn.current = true;
              } catch (err) {
                console.warn("Auto-start camera for saved VB failed", err);
              }
            }
            
            if (attempts < maxAttempts) {
              setTimeout(checkAndApply, 250);
            }
            return;
          }
          
          // Ensure model is loaded
          if (!selfieSegmentation.current) {
            try {
              await preloadModel();
            } catch (err) {
              console.warn("Failed to preload model for saved background", err);
              return;
            }
          }
          
          // Now apply the background
          if (savedBackground === "blur") {
            selfieSegmentationPreview(true);
          } else {
            loadImageToCanvas(savedBackground);
          }
        };
        
        // Start checking
        checkAndApply();
      };
      
      applyBackgroundWhenReady();
    }
  }, [preloadModel]);

  const handleTestAudio = async () => {
    if (audioTestState !== "idle") return;

    try {
      setAudioTestState("recording");
      
      // 1. Get Mic Stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: audioPreference.current ? { exact: audioPreference.current } : undefined }
      });

      // 2. Record
      const mediaRecorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop mic tracks
        stream.getTracks().forEach(track => track.stop());

        // Create blob
        const blob = new Blob(chunks, { type: "audio/webm" });
        const audioURL = URL.createObjectURL(blob);
        
        // 3. Playback
        const audioEl = audioPreviewRef.current;
        if (audioEl) {
          setAudioTestState("playing");
          audioEl.src = audioURL;
          
          // Set output device
          if (audioOutputPreference.current && (audioEl as any).setSinkId) {
             try {
               await (audioEl as any).setSinkId(audioOutputPreference.current);
             } catch(e) { console.warn(e); }
          }

          audioEl.onended = () => {
            setAudioTestState("idle");
            URL.revokeObjectURL(audioURL);
          };
          
          try {
            await audioEl.play();
          } catch (e) {
            console.error(e);
            setAudioTestState("idle");
            showAlert("Playback failed", "danger");
          }
        } else {
           setAudioTestState("idle");
        }
      };

      mediaRecorder.start();
      
      // Record for 3 seconds
      setTimeout(() => {
        if (mediaRecorder.state !== "inactive") {
          mediaRecorder.stop();
        }
      }, 3000);

    } catch (error) {
      console.error("Audio test failed", error);
      setAudioTestState("idle");
      showAlert("Failed to access microphone for testing.", "danger");
    }
  };

  const selfieSegmentationPreview = async (doSegmentation: boolean) => {
    const refVideo = captureVideoRef.current;
    const previewVideo = videoOutputPreviewRef.current;
    const virtualImage = new Image();
    virtualImage.crossOrigin = "anonymous";
    const useBlur = isVirtualBackgroundBlur(selectedImage.current);
    virtualImage.src = useBlur ? "" : (selectedImage.current || "");
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas || !refVideo || !previewVideo) return;
    const mediaCanvas = mainCanvas;
    let ctx = mediaCanvas.getContext("2d");
    pauseSegmentation.current = false;
    if (!doSegmentation) {
      keepSegmentationRunning.current = false;
      processedStream.current?.getVideoTracks().forEach((t) => t.stop());
      processedStream.current = null;
      pauseSegmentation.current = true;
      previewVideo.srcObject = refVideo.srcObject;
      previewVideo.classList.remove("d-none");
      setCookie("useBackground", "false", 7);
      setCookie("backgroundSrc", "", 7);
      return;
    }

    keepSegmentationRunning.current = true;
    // Cancel any previous animation frame
    if (animationFrameId.current !== null) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    const segmentImage = async (videoElement: HTMLVideoElement) => {
      try {
        const processFrame = async () => {
          // Early exit if cleanup has started
          if (!keepSegmentationRunning.current || pauseSegmentation.current) {
            animationFrameId.current = null;
            return;
          }
          // Validate model and video element
          const model = selfieSegmentation.current;
          if (
            !model ||
            !videoElement ||
            videoElement.videoWidth === 0 ||
            videoElement.videoHeight === 0
          ) {
            animationFrameId.current = null;
            return;
          }
          try {
            // Double-check before send to avoid race
            if (keepSegmentationRunning.current && !pauseSegmentation.current && selfieSegmentation.current) {
              await model.send({ image: videoElement });
            }
          } catch (error) {
            // Model may have been closed - stop the loop
            console.error("SelfieSegmentation send error:", error);
            keepSegmentationRunning.current = false;
            animationFrameId.current = null;
            return;
          }
          
          if (keepSegmentationRunning.current && !pauseSegmentation.current) {
            animationFrameId.current = requestAnimationFrame(processFrame);
          } else {
            animationFrameId.current = null;
          }
        };
        videoElement.onloadeddata = () => {
          if (keepSegmentationRunning.current) {
            animationFrameId.current = requestAnimationFrame(processFrame);
          }
        };
        setTimeout(async () => {
          processedStream.current = mediaCanvas.captureStream(frameRate || 5);
          previewVideo.srcObject = processedStream.current;
          previewVideo.classList.remove("d-none");
          if (previewVideo.paused) {
            try {
              await previewVideo.play();
            } catch (error) {
              console.log("preview play", error);
            }
          }
        }, 100);
      } catch (error) {
        console.log("segmentImage", error);
      }
    };

    if (videoAlreadyOn.current) {
      if (
        clonedTrack.current &&
        clonedTrack.current.readyState === "live" &&
        localStreamVideo.current?.getVideoTracks()[0]?.label === clonedTrack.current.label
      ) {
        // reuse
      } else {
        const localTrack = localStreamVideo.current?.getVideoTracks()[0];
        if (localTrack) {
          clonedTrack.current = localTrack.clone();
          clonedStream.current = new MediaStream([clonedTrack.current]);
        }
        segmentVideo.current = clonedStream.current || null;
      }
      refVideo.srcObject = segmentVideo.current;
      if (refVideo.paused) {
        try {
          await refVideo.play();
        } catch (error) {
          console.log("capture play", error);
        }
      }
      refVideo.width = segmentVideo.current?.getVideoTracks()[0]?.getSettings().width || 0;
      refVideo.height = segmentVideo.current?.getVideoTracks()[0]?.getSettings().height || 0;
      mediaCanvas.width = refVideo.width;
      mediaCanvas.height = refVideo.height;
      ctx = mediaCanvas.getContext("2d");
      try {
        if (doSegmentation) {
          await segmentImage(refVideo);
          setCookie("useBackground", "true", 7);
          setCookie("backgroundSrc", selectedImage.current || "", 7);
        } else {
          previewVideo.srcObject = clonedStream.current || localStreamVideo.current;
        }
      } catch (error) {
        console.log("segmenting", error);
      }
    } else {
      if (segmentVideo.current && segmentVideo.current.getVideoTracks()[0]?.readyState === "live") {
        // reuse
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: meetingParams.current.videoDeviceId as any },
            audio: false,
          });
          segmentVideo.current = stream;
          refVideo.srcObject = segmentVideo.current;
          if (refVideo.paused) {
            try {
              await refVideo.play();
            } catch (error) {
              console.log("capture play 2", error);
            }
          }
        } catch (error) {
          console.log("getUserMedia for segmentation", error);
        }
        refVideo.width = segmentVideo.current?.getVideoTracks()[0]?.getSettings().width || 0;
        refVideo.height = segmentVideo.current?.getVideoTracks()[0]?.getSettings().height || 0;
        mediaCanvas.width = refVideo.width;
        mediaCanvas.height = refVideo.height;
        ctx = mediaCanvas.getContext("2d");
      }
      try {
        if (doSegmentation) {
          await segmentImage(refVideo);
          setCookie("useBackground", "true", 7);
          setCookie("backgroundSrc", selectedImage.current || "", 7);
        } else {
          previewVideo.srcObject = refVideo.srcObject;
        }
      } catch (error) {
        setCookie("useBackground", "false", 7);
        setCookie("backgroundSrc", "", 7);
      }
    }

    let repeatPattern: "repeat" | "repeat-x" | "repeat-y" | "no-repeat" = "no-repeat";
    try {
      if (
        !useBlur && (virtualImage.width < mediaCanvas.width ||
        virtualImage.height < mediaCanvas.height
      )) {
        repeatPattern = "repeat";
      }
    } catch (error) {
      console.log("pattern", error);
    }

    const onResults = (results: any) => {
      try {
        if (
          !pauseSegmentation.current &&
          mediaCanvas &&
          mediaCanvas.width > 0 &&
          mediaCanvas.height > 0
        ) {
          compositeVirtualBackgroundFrame({
            ctx: ctx!,
            segmentationMask: results.segmentationMask,
            sourceImage: results.image,
            backgroundImage: !useBlur && virtualImage.src && virtualImage.width > 0 && virtualImage.height > 0
              ? virtualImage
              : null,
            width: mediaCanvas.width,
            height: mediaCanvas.height,
            repeatPattern,
            blurFallbackPixels: useBlur ? DEFAULT_BACKGROUND_BLUR_PIXELS : 0,
          });
        }
      } catch (error) {
        console.log("apply background", error);
      }
    };

    if (!selfieSegmentation.current) {
      await preloadModel().catch(() => console.log("preload model error"));
      selfieSegmentationPreview(doSegmentation);
    }
    try {
      selfieSegmentation.current?.onResults(onResults);
    } catch (error) {
      console.log("set onResults", error);
    }
  };

  const changeContent = (nextType: MeetingParams["type"]) => {
    setActType(nextType);
  };

  const populateDuration = useCallback(() => {
    // durationOptions already derived; keep for parity
  }, []);

  const [scheduledDateState, setScheduledDateState] = useState<number | null>(null);

  const onDateChange = (value: string) => {
    selectedTime.current = value;
    if (!value) {
      eventScheduled.current = false;
      scheduledDate.current = 0;
      setScheduledDateState(null);
      return;
    }
    const date = new Date(value).getTime();
    if (isNaN(date)) {
      eventScheduled.current = false;
      scheduledDate.current = 0;
      setScheduledDateState(null);
      return;
    }
    eventScheduled.current = true;
    scheduledDate.current = date;
    setScheduledDateState(date);
  };

  const handleRecordingParamChange = (
    paramName: keyof RecordingParams,
    paramType: "boolean" | "number" | "string"
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let selectedValue: any = e.target.value;
    const refValue = refRecordingParams.current[paramName];
    if (paramType === "boolean") {
      selectedValue = selectedValue === "true";
      if (selectedValue && !refValue) {
        e.target.value = "false";
        showAlert("Enabling of this feature is not supported.", "danger");
        return;
      }
    }
    if (paramType === "number") {
      selectedValue = parseInt(selectedValue, 10);
      if (Number.isNaN(selectedValue)) {
        showAlert(`Please enter a valid number for ${paramName}.`, "warning");
        e.target.value = refValue.toString();
        return;
      }
      if (selectedValue > (refValue as number)) {
        showAlert(`This cannot be greater than ${refValue}.`, "warning");
        e.target.value = refValue.toString();
        return;
      }
    }
    if (paramType === "string") {
      if (paramName === "recordingPreferredOrientation") {
        const allowed = ["landscape", "portrait"];
        if (!allowed.includes(selectedValue)) {
          showAlert("Invalid orientation selected.", "warning");
          e.target.value = refValue as string;
          return;
        }
      }
    }
    (recordingParams.current as any)[paramName] = selectedValue;
  };

  const handleMeetingParamChange = (
    paramName: keyof MeetingParams,
    paramType: "boolean" | "number" | "string"
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let selectedValue: any = e.target.value;
    const refValue = refMeetingParams.current[paramName];
    if (paramType === "boolean") selectedValue = selectedValue === "true";
    if (paramType === "number") {
      selectedValue = parseInt(selectedValue, 10);
      if (Number.isNaN(selectedValue)) {
        showAlert(`Please enter a valid number for ${paramName}.`, "warning");
        e.target.value = (refValue as number).toString();
        return;
      }
      if (selectedValue > (refValue as number)) {
        showAlert(`This cannot be greater than ${refValue}.`, "warning");
        e.target.value = (refValue as number).toString();
        return;
      }
    }
    if (paramType === "string") {
      if (paramName === "mediaType") {
        if (selectedValue === "video" && refValue !== "video") {
          showAlert(`This cannot be changed to ${selectedValue}.`, "warning");
          e.target.value = refValue as string;
          return;
        }
      }
      if (paramName === "targetResolution" || paramName === "targetResolutionHost") {
        const refMedia = refMeetingParams.current.mediaType || "video";
        if (refMedia === "video") {
          const resolutions = ["qnhd", "sd", "hd", "fhd", "qhd"];
          const selectedIndex = resolutions.indexOf(selectedValue.toLowerCase());
          const refIndex = resolutions.indexOf((refValue as string).toLowerCase());
          if (selectedIndex > refIndex) {
            showAlert(`This cannot be changed to ${selectedValue}.`, "warning");
            e.target.value = refValue as string;
            return;
          }
        }
      }
    }
    (meetingParams.current as any)[paramName] = selectedValue;
    forceRender();
  };

  const checkData = (incomingData: any, urlData = "") => {
    if (incomingData?.apiKey && incomingData?.apiUserName) {
      if (
        typeof incomingData.apiKey === "string" &&
        incomingData.apiKey.length >= 32 &&
        typeof incomingData.apiUserName === "string" &&
        incomingData.apiUserName.length >= 6
      ) {
        Cookies.set(
          "apiRoomCredsData",
          JSON.stringify({
            apiKey: incomingData.apiKey,
            apiUserName: incomingData.apiUserName,
            subusername: incomingData.subusername || null,
            eventType: incomingData.eventType || "conference",
            url: incomingData.url || "",
          }),
          { expires: 1 / 24 }
        );
        setApiKey(incomingData.apiKey);
        setApiUserName(incomingData.apiUserName);
        if (incomingData.subusername) setSubUserName(incomingData.subusername);
        if (incomingData.eventType) {
          const lowered = (incomingData.eventType as string).toLowerCase() as MeetingParams["type"];
          if (["chat", "broadcast", "webinar", "conference"].includes(lowered)) {
            setActType(lowered);
            changeContent(lowered);
          }
        }
        const _url = new URL(urlData || window.location.href);
        socket.current?.emit(
          "checkAPI",
          {
            apiKey: incomingData.apiKey,
            apiUserName: incomingData.apiUserName,
            hostName: _url.hostname,
          },
          ({ success, meetingRoomParams_, recordingParams_, apiType_, recordingMinutes, meetingMinutes, capacityLimits: capacityLimits_ }: any) => {
            const prevAPIType = apiType;
            if (apiType_ === "production" && !isValidDomain(_url.hostname, _url.protocol)) {
              showAlert(
                "Invalid domain for production, please use a secured valid domain.",
                "danger"
              );
              setApiKey(null);
              setApiUserName(null);
              setShowRecording(false);
              return;
            }

            // Always apply capacity limits if returned, even when success=false
            // (demo mode still returns limits like {broadcast: 100, conference: 8, ...})
            if (capacityLimits_ && typeof capacityLimits_ === 'object') {
              capacityLimits.current = capacityLimits_;
              const limit = capacityLimits_[actType] ?? capacityLimits_['other'] ?? 8;
              const maxCap = actType === 'chat' ? 2 : limit;
              setRefRoomCapacity(maxCap);
              // In quick setup, auto-set capacity to roughly half (chat always 2)
              if (quickSetup) {
                const initCap = actType === 'chat' ? 2 : Math.max(2, Math.ceil(maxCap / 2));
                setCapacity(initCap);
              }
            }

            if (success && meetingMinutes > 0) {
              setAlertDemo(true);
              setShowRecording(recordingMinutes > 0);
              if (apiType_ === "sandbox") {
                setApiType("sandbox");
              } else if (apiType_ === "production") {
                setApiType("production");
              }

              // Skip updateAPI — only checkAPI is needed
              const doUpdateAPI = false;

              if (prevAPIType !== apiType_) {
                socket.current?.disconnect();
                let newSocketUrl = "";
                if (apiType_ === "sandbox") {
                  newSocketUrl = getSocketUrl('s', useFixedLink);
                  setSafeRoom(false);
                  setMaxMeetingDuration(3);
                } else if (apiType_ === "production") {
                  newSocketUrl = getSocketUrl('p', useFixedLink);
                }
                const newSocket = io(newSocketUrl, { transports: ["websocket"], auth: {pathname: window.location.pathname} });
                socket.current = newSocket;
                if (doUpdateAPI) {
                  newSocket.emit(
                    "updateAPI",
                    {
                      apiUserName: incomingData.apiUserName,
                      meetingRoomParams: meetingRoomParams_,
                      recordingParams: recordingParams_,
                    },
                    (updatedData: any) => {
                      if (
                        updatedData &&
                        updatedData.meetingRoomParams &&
                        updatedData.recordingParams
                      ) {
                        meetingParams.current = { ...updatedData.meetingRoomParams, type: actType };
                        recordingParams.current = updatedData.recordingParams;
                        refMeetingParams.current = updatedData.meetingRoomParams;
                        refRecordingParams.current = updatedData.recordingParams;
                      }
                    }
                  );
                }
                if (apiType_ === "production") setMaxMeetingDuration(24);
                populateDuration();
              }
              refMeetingParams.current = meetingRoomParams_;
              refRecordingParams.current = recordingParams_;
              meetingParams.current = { ...meetingRoomParams_, type: actType };
              recordingParams.current = recordingParams_;
              // Sync itemPageLimit state from server response
              if (meetingRoomParams_?.itemPageLimit != null) {
                setItemPageLimit(meetingRoomParams_.itemPageLimit);
              }
            } else {
              setApiKey(null);
              setApiUserName(null);
              setShowRecording(false);
            }
          }
        );
      }
    }
  };

  useEffect(() => {
    createLoadingModal();
    const socketInstance = io(defaultURL.current, { transports: ["websocket"], auth: {pathname: window.location.pathname} });
    socket.current = socketInstance;
    socketInstance.on("connection-success", () => {
      hideLoadingModal();
      if (finalDataRef.current && !dataChanged.current) {
        checkData(finalDataRef.current, window.location.href);
        dataChanged.current = true;
      }
    });
    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Detect /meeting/xxx URL pattern and auto-validate the meeting
  useEffect(() => {
    const pathname = window.location.pathname;
    const meetingMatch = pathname.match(/^\/meeting\/([a-zA-Z0-9]+)$/);
    if (meetingMatch && meetingMatch[1]) {
      const urlMeetingID = meetingMatch[1].toLowerCase();
      if (validateMeetingID(urlMeetingID)) {
        setMeetingID(urlMeetingID);
        setUrlMeetingMode(true);
      }
    }
  }, []);

  // Auto-validate meeting when in URL meeting mode and socket is connected
  useEffect(() => {
    if (!urlMeetingMode || urlMeetingValidated || !socket.current?.connected || !meetingID) {
      return;
    }

    // Determine the correct socket URL based on meeting ID prefix
    const newSocketUrl = getSocketUrl(meetingID.charAt(0), useFixedLink);

    // Reconnect to correct socket if needed
    if (newSocketUrl && defaultURL.current !== newSocketUrl) {
      socket.current?.disconnect();
      const newSocket = io(newSocketUrl, { transports: ["websocket"], auth: {pathname: window.location.pathname} });
      socket.current = newSocket;
      defaultURL.current = newSocketUrl;
      newSocket.on("connection-success", () => {
        validateUrlMeeting(meetingID);
      });
    } else {
      validateUrlMeeting(meetingID);
    }
  }, [urlMeetingMode, urlMeetingValidated, meetingID]);

  // Function to validate meeting from URL
  const validateUrlMeeting = (urlMeetingID: string) => {
    setLoading(true);
    socket.current?.emit(
      "getRoomDetails",
      { meetingID: urlMeetingID },
      ({
        exists,
        names,
        bans,
        meetingCapacity,
        meetingEnded,
        meetingStarted,
        adminName,
        scheduledDate,
        pending,
        secureCode: secureCode_,
        waitRoom: waitRoom_,
        checkAd,
      }: any) => {
        setLoading(false);
        setUrlMeetingValidated(true);
        
        if (exists) {
          setPassWord(secureCode_);
          setWaitingForAdmin(false);
          setAdminNamed(adminName);
          setPender(pending);
          setWaitRoom(waitRoom_);
          setCheckForAdmin(checkAd);
          setExistingNames(names);
          setExistingBans(bans);
          
          if (meetingEnded) {
            setWarningMessage("This event has already ended.");
            showAlert("This event has already ended.", "danger");
            setUrlMeetingMode(false); // Fall back to normal flow
            return;
          }
          
          if (!pending) {
            if (!meetingStarted) setAdminStarted(true);
            else setAdminStarted(false);
            const parsedCap = parseInt(meetingCapacity, 10);
            const diff = names.length - bans.length;
            if (diff >= parsedCap) {
              setWarningMessage("Event room is already at capacity.");
              showAlert("Event room is already at capacity.", "warning");
              setUrlMeetingMode(false);
              return;
            }
          } else {
            const currentDate = new Date();
            const scheduledMeetingDate = new Date(scheduledDate);
            const diff = scheduledMeetingDate.getTime() - currentDate.getTime();
            const minutes = Math.floor(diff / 1000 / 60);
            if (minutes > 5) {
              setWarningMessage("This meeting has not started yet, you can join 5 minutes to time.");
              showAlert(`Meeting will start at ${scheduledMeetingDate.toLocaleString()}`, "info");
              setUrlMeetingMode(false);
              return;
            } else {
              const parsedCap = parseInt(meetingCapacity, 10);
              const diff2 = names.length - bans.length;
              if (diff2 > parsedCap) setDeferAlertForCapacity(true);
              setWaitingForAdmin(true);
            }
          }
          
          // Meeting is valid - show success and go to step 2 (name entry)
          showAlert("Meeting found! Please enter your name to join.", "success");
          setCurrentStep(2);
          userNameInputMainRef.current?.focus();
          if (userNameInputMainRef.current) {
            userNameInputMainRef.current.readOnly = false;
          }
        } else {
          setWarningMessage("The meeting ID from the URL does not exist.");
          showAlert("Meeting not found. Please check the link.", "danger");
          setUrlMeetingMode(false); // Fall back to normal flow
        }
      }
    );
  };

  useEffect(() => {
    if (!finalDataRef.current && !dataChanged.current) {
      const raw = Cookies.get("apiRoomCredsData");
      let apiRoomCredsData: any = null;
      try {
        apiRoomCredsData = raw ? JSON.parse(raw) : null;
      } catch (e) {
        apiRoomCredsData = null;
      }
      if (apiRoomCredsData?.apiKey && apiRoomCredsData?.apiUserName) {
        const loaded = {
          apiKey: apiRoomCredsData.apiKey,
          apiUserName: apiRoomCredsData.apiUserName,
          subusername: apiRoomCredsData.subusername || null,
          eventType: apiRoomCredsData.eventType || "conference",
          url: apiRoomCredsData.url || "",
        };
        setTimeout(() => {
          finalDataRef.current = loaded;
        }, 250);
      }
    }
  }, []);

  useEffect(() => {
    if (
      finalDataRef.current &&
      socket.current &&
      (socket.current as any).id &&
      socket.current.connected &&
      !dataChanged.current
    ) {
      const dataToUse = data && data.apiKey && data.apiUserName ? data : finalDataRef.current;
      if (dataToUse) {
        checkData(dataToUse, window.location.href);
        dataChanged.current = true;
      }
    }
  }, [finalDataRef.current]);

  useEffect(() => {
    if (eventType) {
      setActType(eventType);
      changeContent(eventType);
      meetingParams.current = { ...meetingParams.current, type: eventType };
    }
  }, [eventType]);

  useEffect(() => {
    if (meetingParams.current?.type) {
      setActType(meetingParams.current.type);
      changeContent(meetingParams.current.type);
    }
  }, [apiType]);

  const checkJoinRateLimit = () => {
    const roomName = meetingID.trim();
    if (!roomName) return true;

    const cookieVal = getCookie(`join_attempts_timestamps_${roomName}`);
    const timestamps = cookieVal ? cookieVal.split(",") : [];
    const now = Date.now();

    // Check 24-hour expiration
    const lastModified = parseInt(getCookie(`join_attempts_last_modified_${roomName}`) || "0", 10);
    if (now - lastModified > 86400000) {
      setCookie(`join_attempts_timestamps_${roomName}`, "", -1);
      setCookie(`join_attempts_last_modified_${roomName}`, "", -1);
      return true;
    }

    if (timestamps.length === 0) return true;

    // Sort timestamps (oldest first)
    const sortedTimestamps = timestamps
      .map((t) => parseInt(t, 10))
      .filter((t) => t > 0)
      .sort((a, b) => a - b);

    const attempts = sortedTimestamps.length;
    const firstAttempt = sortedTimestamps[0];

    if (attempts === 1) {
      // 2nd attempt: 1 minute after first
      if (now - firstAttempt < 60000) return false;
    } else if (attempts === 2) {
      // 3rd attempt: 5 minutes after first
      if (now - firstAttempt < 300000) return false;
    } else if (attempts >= 3) {
      // Max attempts reached
      return false;
    }

    return true;
  };

  const recordJoinDenial = () => {
    const roomName = meetingID.trim();
    if (!roomName) return;

    const now = Date.now();

    // Check 24-hour expiration to reset if needed
    const lastModified = parseInt(getCookie(`join_attempts_last_modified_${roomName}`) || "0", 10);
    if (now - lastModified > 86400000) {
      setCookie(`join_attempts_timestamps_${roomName}`, "", -1);
      setCookie(`join_attempts_last_modified_${roomName}`, "", -1);
    }

    const cookieVal = getCookie(`join_attempts_timestamps_${roomName}`);
    const timestamps = cookieVal ? cookieVal.split(",") : [];
    timestamps.push(now.toString());

    setCookie(`join_attempts_timestamps_${roomName}`, timestamps.join(","), 1); // 1 day expiry
    setCookie(`join_attempts_last_modified_${roomName}`, now.toString(), 1);
  };

  useEffect(() => {
    const handleExitWaitRoomUser = ({ typed, name }: any) => {
      if (typed) {
        socket.current?.emit(
          "exitWaitRoomURL",
          { meetingID, userName: name, secret: userSecret },
          ({ success, url }: any) => {
            setUrll(url);
            setWaitingModalVisible(false);
            if (success) {
              showAlert(
                "Host has allowed you entry. Redirecting to meeting room in 2 seconds.",
                "success"
              );
              setTimeout(() => {
                const currentSelfieSegmentation = selfieSegmentation.current;
                cleanupAndNavigate(() => {
                  if (onJoin) {
                    onJoin({
                      meetingID,
                      userName: name,
                      secureCode: "",
                      url,
                      secret: userSecret,
                      audioPreference: audioPreference.current,
                      videoPreference: videoPreference.current,
                      audioOutputPreference: audioOutputPreference.current,
                      virtualBackground: selectedImage.current,
                      facingMode: currentFacingMode.current,
                      isDarkMode: isDarkMode,
                      eventType: actType,
                      selfieSegmentation: currentSelfieSegmentation,
                    } as any);
                  } else {
                    window.location.href = url;
                  }
                });
              }, 2000);
            } else {
              showAlert("You cannot join this event.", "danger");
            }
          }
        );
      } else {
        recordJoinDenial();
        setWaitingModalVisible(false);
        showAlert("Host has denied you entry.", "danger");
      }
    };

    const handleExitWaitRoom = ({ name }: any) => {
      if (waitingForAdmin || checkForAdmin) {
        if ((waitingForAdmin && !waitRoom) || checkForAdmin) {
          socket.current?.emit(
            "exitWaitRoomURL",
            { meetingID, userName: name, secret: userSecret },
            ({ success, url }: any) => {
              setWaitingModalVisible(false);
              if (success) {
                showAlert(
                  "Host has started the meeting and/or allowed you entry. Redirecting in 2 seconds.",
                  "success"
                );
                setTimeout(() => {
                  const currentSelfieSegmentation = selfieSegmentation.current;
                  cleanupAndNavigate(() => {
                    if (onJoin) {
                      onJoin({
                        meetingID,
                        userName: name,
                        secureCode: "",
                        url,
                        secret: userSecret,
                        audioPreference: audioPreference.current,
                        videoPreference: videoPreference.current,
                        audioOutputPreference: audioOutputPreference.current,
                        virtualBackground: selectedImage.current,
                        isDarkMode: isDarkMode,
                        eventType: actType,
                        selfieSegmentation: currentSelfieSegmentation,
                      } as any);
                    } else {
                      window.location.href = url;
                    }
                  });
                }, 2000);
              } else {
                showAlert("You cannot join this event.", "danger");
              }
            }
          );
        } else {
          setWaitingForAdmin(false);
        }
      }
    };

    socket.current?.on("exitWaitRoomUser", handleExitWaitRoomUser);
    socket.current?.on("exitWaitRoom", handleExitWaitRoom);
    return () => {
      socket.current?.off("exitWaitRoomUser", handleExitWaitRoomUser);
      socket.current?.off("exitWaitRoom", handleExitWaitRoom);
    };
  }, [waitingForAdmin, checkForAdmin, waitRoom, meetingID, userSecret, apiType, maxMeetingDuration, showAlert]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      checkData(event.data, event.origin);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [actType]);

  useEffect(() => {
    preloadModel().catch((err) => console.log("Error preloading model", err));
  }, []);

  // Auto-initialize media on first user interaction if not already active
  useEffect(() => {
    if (initMedia) return;

    const handleInteraction = () => {
      setInitMedia(true);
    };

    window.addEventListener("click", handleInteraction, { once: true });
    return () => window.removeEventListener("click", handleInteraction);
  }, [initMedia]);

  useEffect(() => {
    async function startMedia() {
      setLoading(true);
      await streamSuccessNull();
      await previewMedia();
      setLoading(false);
      setInitMedia(true);
    }
    if (initMedia) startMedia();
  }, [initMedia, previewMedia, streamSuccessNull]);

  const cleanupMedia = useCallback(() => {
    try {
      // Stop the animation loop immediately - set flags BEFORE anything else
      keepSegmentationRunning.current = false;
      pauseSegmentation.current = true;
      
      // Cancel any pending animation frame
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      
      // Clear the local model reference but DON'T close the singleton model
      // The model is shared across components and will be reused in the Room
      selfieSegmentation.current = null;
      
      // Stop all media tracks
      localStreamVideo.current?.getTracks().forEach((t) => t.stop());
      processedStream.current?.getTracks().forEach((t) => t.stop());
      clonedStream.current?.getTracks().forEach((t) => t.stop());
      clonedTrack.current?.stop();
      segmentVideo.current?.getTracks().forEach((t) => t.stop());
      
      socket.current?.disconnect();
    } catch (err) {
      // Swallow cleanup errors to avoid unmount crashes
    }
  }, []);

  // Helper to cleanup and wait before navigation
  const cleanupAndNavigate = useCallback((callback: () => void) => {
    cleanupMedia();
    // Give a small delay for any pending microtasks to see the nullified model
    setTimeout(callback, 50);
  }, [cleanupMedia]);

  useEffect(() => {
    return () => {
      cleanupMedia();
    };
  }, [cleanupMedia]);

  const handleConfirmMeetingIDClick = () => {
    const cleanMeetingID = meetingID.toLowerCase().trim();
    if (!validateMeetingID(cleanMeetingID)) {
      setWarningMessage("Enter a valid Event ID (minimum 7 characters, alphanumeric only).");
      return;
    }
    if (meetingID.length > 6) {
      setMeetingID(meetingID.toLowerCase());
      setWarningMessage("");
      setLoading(true);
      const newSocketUrl = getSocketUrl(meetingID.charAt(0), useFixedLink);
      let newSocket = socket.current;
      if (defaultURL.current !== newSocketUrl) {
        socket.current?.disconnect();
        newSocket = io(newSocketUrl, { transports: ["websocket"], auth: {pathname: window.location.pathname} });
        socket.current = newSocket;
        defaultURL.current = newSocketUrl;
      }
      newSocket?.emit(
        "getRoomDetails",
        { meetingID: meetingID.toLowerCase() },
        ({
          exists,
          names,
          bans,
          meetingCapacity,
          meetingEnded,
          meetingStarted,
          adminName,
          scheduledDate,
          pending,
          secureCode: secureCode_,
          waitRoom: waitRoom_,
          checkAd,
        }: any) => {
          setLoading(false);
          setPassWord(secureCode_);
          setWaitingForAdmin(false);
          setAdminNamed(adminName);
          setPender(pending);
          setWaitRoom(waitRoom_);
          setCheckForAdmin(checkAd);
          if (exists) {
            setExistingNames(names);
            setExistingBans(bans);
            if (!pending) {
              if (!meetingStarted) setAdminStarted(true);
              else setAdminStarted(false);
              const parsedCap = parseInt(meetingCapacity, 10);
              const diff = names.length - bans.length;
              if (diff >= parsedCap) {
                setWarningMessage("Event room is already at capacity");
                return;
              }
            } else {
              const currentDate = new Date();
              const scheduledMeetingDate = new Date(scheduledDate);
              const diff = scheduledMeetingDate.getTime() - currentDate.getTime();
              const minutes = Math.floor(diff / 1000 / 60);
              if (minutes > 5) {
                setWarningMessage("This meeting has not started yet, you can join 5 minutes to time.");
                showAlert(`Meeting will start at ${scheduledMeetingDate.toLocaleString()}`, "danger");
                return;
              } else {
                const parsedCap = parseInt(meetingCapacity, 10);
                const diff2 = names.length - bans.length;
                if (diff2 > parsedCap) setDeferAlertForCapacity(true);
                setWaitingForAdmin(true);
              }
            }
            if (meetingEnded) {
              setWarningMessage("This event has already ended");
              return;
            }
            setWarningMessage("The event ID is valid. Proceed to enter your name.");
            userNameInputMainRef.current?.focus();
            userNameInputMainRef.current && (userNameInputMainRef.current.readOnly = false);
            setCurrentStep(2);
          } else {
            setWarningMessage("The Event ID does not exist. Enter a valid Event ID.");
            if (userNameInputMainRef.current) {
              userNameInputMainRef.current.readOnly = true;
              userNameInputMainRef.current.value = "";
            }
          }
        }
      );
    } else {
      setWarningMessage("Enter a valid Event ID.");
      if (userNameInputMainRef.current) userNameInputMainRef.current.readOnly = true;
    }
  };

  const handleJoinMeetingClick = () => {
    if (userName.length === 0 || userNameInputMainRef.current?.readOnly) {
      setNameWarn("Enter an Event ID and click confirm first.");
      return;
    }
    if (userName.length < 2 || userName.length > 10) {
      setNameWarn("Name must be between 2 and 10 characters long.");
      return;
    }
    if (isReservedKeyword(userName)) {
      setNameWarn("Name cannot be a reserved keyword.");
      return;
    }
    const named = userName.replace(/[^\w\s]/gi, "").replace(/\s/g, "");
    if (existingBans.includes(named)) {
      setNameWarn("You are banned from this event.");
      return;
    }
    if (adminStarted && named !== adminNamed) {
      setNameWarn("Host has not started the event yet.");
      return;
    }
    if (existingNames.some((existing) => existing.toLowerCase() === named.toLowerCase())) {
      if (adminNamed !== named) {
        setNameWarn("Name is already taken.");
        return;
      } else if (!pender) {
        setNameWarn("Name is already taken.");
        return;
      }
    }
    setNameWarn("");
    if (deferAlertForCapacity && named !== adminNamed) {
      showAlert("Event room is full, try again later.", "danger");
      return;
    }

    if (!checkJoinRateLimit()) {
      setNameWarn("Too many attempts. Please wait.");
      return;
    }

    setLoading(true);
    socket.current?.emit(
      "joinTempRoom",
      {
        meetingID,
        userName: named,
        secureCode: null,
        videoPreference: videoPreference.current,
        audioPreference: audioPreference.current,
        audioOutputPreference: audioOutputPreference.current,
      },
      ({ success, secret, url }: any) => {
        setLoading(false);
        setUserSecret(secret);
        setUrll(url);
        if (success) {
          if (waitingForAdmin || waitRoom || checkForAdmin) {
            if (named === adminNamed) {
              setAdminWelcomeModalVisible(true);
              setWarningMessage("");
              setWaitingForAdmin(false);
            } else {
              setWaitingModalVisible(true);
            }
          } else {
            if (onJoin) {
              cleanupAndNavigate(() => {
                onJoin({
                  meetingID,
                  userName: named,
                  secureCode: "",
                  url,
                  secret,
                  audioPreference: audioPreference.current,
                  videoPreference: videoPreference.current,
                  audioOutputPreference: audioOutputPreference.current,
                  virtualBackground: selectedImage.current,
                  isDarkMode: isDarkMode,
                  eventType: actType,
                  selfieSegmentation: selfieSegmentation.current,
                } as any);
              });
            } else {
              setCodeVisible(true);
              setExpandMeetingParams(false);
              setExpandRecordingParams(false);
            }
          }
        } else {
          setCodeVisible(false);
          setWarningMessage("The Event could not be joined, retry!");
          showAlert("The Event could not be joined, retry!", "danger");
        }
      }
    );
  };

  const checkPasscodeRateLimit = () => {
    const cookieVal = getCookie("passcode_attempts_timestamps");
    const timestamps = cookieVal ? cookieVal.split(",") : [];
    const now = Date.now();

    // Filter out attempts older than 1 hour
    const validTimestamps = timestamps.filter((t) => {
      const timestamp = parseInt(t, 10);
      return now - timestamp < 3600000; // 1 hour
    });

    // Update cookie if filtered
    if (validTimestamps.length !== timestamps.length) {
      setCookie("passcode_attempts_timestamps", validTimestamps.join(","), 1);
    }

    // Check limits
    const attemptsLastHour = validTimestamps.length;
    const attemptsLastMinute = validTimestamps.filter((t) => {
      const timestamp = parseInt(t, 10);
      return now - timestamp < 60000; // 1 minute
    }).length;

    if (attemptsLastHour >= 10 || attemptsLastMinute >= 5) {
      return false;
    }
    return true;
  };

  const recordPasscodeFailure = () => {
    const cookieVal = getCookie("passcode_attempts_timestamps");
    const timestamps = cookieVal ? cookieVal.split(",") : [];
    timestamps.push(Date.now().toString());
    setCookie("passcode_attempts_timestamps", timestamps.join(","), 1);
  };

  const handleSubmitPasscode = () => {
    if (!checkPasscodeRateLimit()) {
      setWarningMessage("Too many attempts. Please try again later.");
      return;
    }

    if (passcodeInput === passWord) {
      setAdminWelcomeModalVisible(false);
      if (onJoin) {
        const currentSelfieSegmentation = selfieSegmentation.current;
        cleanupAndNavigate(() => {
          onJoin({
            meetingID,
            userName: userName + "_2",
            secureCode: passWord,
            url: urll,
            secret: userSecret,
            audioPreference: audioPreference.current,
            videoPreference: videoPreference.current,
            audioOutputPreference: audioOutputPreference.current,
            virtualBackground: selectedImage.current,
            isDarkMode: isDarkMode,
            eventType: actType,
            selfieSegmentation: currentSelfieSegmentation,
          } as any);
        });
      } else {
        // window.location.href = urll;
        setWarningMessage("Join handler missing.");
      }
    } else {
      recordPasscodeFailure();
      setWarningMessage("Incorrect passcode. Try again.");
    }
  };

  const handleStartMeetingClick = async () => {
    const capacityValue = typeof capacity === "string" ? parseInt(capacity, 10) : capacity;
    const missingFields: string[] = [];
    if (!selectedDurationMinutes) missingFields.push('Meeting Duration');
    if (!capacityValue || capacityValue < 1) missingFields.push('Room Capacity');
    if (!userName || userName.trim().length < 2) missingFields.push('Display Name (min 2 chars)');
    if (missingFields.length > 0) {
      showAlert(`Please fill in: ${missingFields.join(', ')}`, "danger");
      return;
    }
    if (capacityValue > refRoomCapacity) {
      showAlert(`Room capacity cannot be more than ${refRoomCapacity}.`, "danger");
      return;
    }
    if (capacityValue < 2) {
      showAlert("Room capacity must be at least 2.", "danger");
      return;
    }
    const userNam = userName.trim();
    if (userNam.length < 2) {
      showAlert("Username must be at least 2 characters long.", "danger");
      return;
    }
    if (isReservedKeyword(userNam)) {
      showAlert("Username cannot be a reserved keyword.", "danger");
      return;
    }
    const meetID = generateMeetingID();
    setUserName(userNam);
    setCapacity(capacityValue);
    const secureCode_ =
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    setSecureCode(secureCode_);
    createOrScheduleMeeting(meetID, secureCode_);
  };

  const createOrScheduleMeeting = async(meetID: string, secCode: string) => {
    const selectedDateTime = selectedTime.current ? moment(selectedTime.current) : null;
    const duration = selectedDurationMinutes;
    const scheduledDate = selectedDateTime?.isValid() ? selectedDateTime.toDate() : null;
    if (meetingID.toLowerCase().startsWith("d") && alertDemo) {
      showAlert(
        "You are about to start a demo event with limited features. Reload in next 15 seconds if you want full event.",
        "warning",
        15000
      );
      setTimeout(async() => {
        await proceedToCreateMeeting(duration, scheduledDate, meetID, secCode);
      }, 15000);
    } else {
      await proceedToCreateMeeting(duration, scheduledDate, meetID, secCode);
    }
  };

  const proceedToCreateMeeting = async(
    duration: number,
    scheduledDate: Date | null,
    meetID: string,
    secCode: string
  ) => {
   
    createLoadingModal();
    const recordingParams_ = { ...recordingParams.current };
    const meetingParams_ = { ...meetingParams.current } as any;
    // Extract top-level fields before removing from meetingRoomParams payload
    const _supportSIP = meetingParams_.supportSIP || false;
    const _directionSIP = meetingParams_.directionSIP || 'outbound';
    const _supportTranslation = meetingParams_.supportTranslation || false;
    const _translationConfigNickName = meetingParams_.translationConfigNickName || '';
    const _supportFlexRoom = meetingParams_.supportFlexRoom || false;
    const _supportMaxRoom = meetingParams_.supportMaxRoom || false;
    const _preferPCMA = meetingParams_.preferPCMA || false;
    const _webhookUrl = meetingParams_.webhookUrl || null;
    const _webhookSecret = meetingParams_.webhookSecret || null;
    // Remove fields that are sent as top-level params
    delete meetingParams_.safeRoom;
    delete meetingParams_.autoStartSafeRoom;
    delete meetingParams_.safeRoomAction;
    delete meetingParams_.supportSIP;
    delete meetingParams_.directionSIP;
    delete meetingParams_.supportTranslation;
    delete meetingParams_.translationConfigNickName;
    delete meetingParams_.supportFlexRoom;
    delete meetingParams_.supportMaxRoom;
    delete meetingParams_.preferPCMA;
    delete meetingParams_.webhookUrl;
    delete meetingParams_.webhookSecret;
    if (meetID.length < 7) {
      generateMeetingID();
    }
    if (meetID.length < 7) {
      showAlert("Meeting ID is required.", "danger");
      hideLoadingModal();
      return;
    }
  
    try {
      socket.current?.emit(
      "createTempRoom",
      {
        meetingID: meetID,
        duration,
        capacity,
        userName,
        scheduledDate,
        secureCode: secCode,
        waitRoom,
        recordingParams: recordingParams_,
        meetingRoomParams: meetingParams_,
        videoPreference: videoPreference.current,
        audioPreference: audioPreference.current,
        audioOutputPreference: audioOutputPreference.current,
        apiKey: apiKey,
        apiUserName: apiUserName,
        subUserName: subusername,
        safeRoom,
        autoStartSafeRoom: false,
        safeRoomAction: null,
        supportSIP: _supportSIP,
        directionSIP: _directionSIP,
        // Translation Support
        supportTranslation: _supportTranslation,
        translationConfigNickName: _translationConfigNickName,
        // Room Capacity Features
        supportFlexRoom: _supportFlexRoom,
        supportMaxRoom: _supportMaxRoom,
        // Audio codec preferences
        preferPCMA: _preferPCMA,
        // Webhook
        webhookUrl: _webhookUrl,
        webhookSecret: _webhookSecret,
      },
      ({ success, secret, url }: any) => {
        hideLoadingModal();
        if (success) {
          setUserSecret(secret);
          setUrll(url);
          const currentTime = moment();
          const selectedDateTime = scheduledDate ? moment(scheduledDate) : null;
          if (selectedDateTime && selectedDateTime.isAfter(currentTime.add(5, "minutes"))) {
            const message = `Event scheduled for later. Host Passcode: ${secCode}  &&  Event ID: ${meetID}`;
            showAlert(message, "success");
            setWarningMessage(message);
            eventScheduled.current = true;
          } else {
            if (onJoin) {
              const currentSelfieSegmentation = selfieSegmentation.current;
              cleanupAndNavigate(() => {
                onJoin({
                  meetingID: meetID,
                  userName: userName + "_2",
                  secureCode: secCode,
                  url,
                  secret,
                  audioPreference: audioPreference.current,
                  videoPreference: videoPreference.current,
                  audioOutputPreference: audioOutputPreference.current,
                  virtualBackground: selectedImage.current,
                  isDarkMode: isDarkMode,
                  eventType: actType,
                  selfieSegmentation: currentSelfieSegmentation,
                } as any);
              });
            } else {
              setCodeVisible(true);
              setExpandMeetingParams(false);
              setExpandRecordingParams(false);
            }
          }
        } else {
          showAlert("The Event could not be scheduled, retry!", "danger");
          setWarningMessage("The Event could not be scheduled, retry!");
        }
      }
    );
    } catch (error) {
      console.log("Error during createTempRoom emit:", error);
      hideLoadingModal();
      showAlert("An error occurred while creating the event. Please try again.", "danger");
    }

  };

  const renderToggle = (
    checked: boolean,
    onChange: (v: boolean) => void,
    disabled?: boolean,
    label?: string
  ) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }} title={label || "Toggle setting"}>
      <div
        onClick={() => !disabled && onChange(!checked)}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          width: 44,
          height: 24,
          background: checked ? MediasfuColors.accent : MediasfuColors.divider,
          borderRadius: 9999,
          position: "relative",
          transition: "all 0.2s ease",
          opacity: disabled ? 0.6 : 1,
        }}
        aria-label={label}
      >
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#fff",
            position: "absolute",
            top: 3,
            left: checked ? 22 : 3,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            transition: "all 0.2s ease",
          }}
        />
      </div>
      <span style={{ color: isDarkMode ? "#e2e8f0" : "#0f172a" }}>
        {checked ? "Enabled" : "Disabled"}
      </span>
    </div>
  );

  // MediaSFU design system: ink-navy (or cool paper) page with one soft teal
  // glow behind the hero and a fainter sky one. Nothing else glows.
  const brand = brandSurface(isDarkMode);
  const accentText = isDarkMode ? MEDIASFU_BRAND.teal : MEDIASFU_BRAND.tealText;
  const displayFont = "'Space Grotesk', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";
  const uiFont = "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

  // The glow lives in <AuroraBackdrop />; the page itself is a flat ink canvas.
  const getGradient = (): string => brand.page;

  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    background: getGradient(),
    color: brand.text,
    fontFamily: uiFont,
    padding: 0,
    boxSizing: "border-box",
    transition: "background 0.2s ease",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    boxShadow: isDarkMode
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "inset 0 1px 0 rgba(255,255,255,0.9)",
  };

  const cardContainerMaxWidth = 1200;


  const mainWrapperStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    margin: "0 auto",
    minHeight: "100%",
    padding: `${MediasfuSpacing.lg}px ${MediasfuSpacing.md}px ${MediasfuSpacing.xl}px`,
    boxSizing: "border-box",
    zIndex: 1,
    maxWidth: "100%",
    // clip, not hidden: hidden makes this a scroll container that never scrolls,
    // which silently disabled the sticky preview and the sticky action bar.
    overflowX: "clip",
    flex: "0 0 auto",
  };





  const sectionTitleStyle: React.CSSProperties = {
    ...MediasfuTypography.getTitleMedium(isDarkMode),
    display: "flex",
    alignItems: "center",
    gap: MediasfuSpacing.sm,
    margin: 0,
    marginBottom: MediasfuSpacing.xs,
    // The task heading must outrank the promotional banner above it.
    fontFamily: displayFont,
    color: brand.text,
    fontSize: "1.4rem",
    fontWeight: 700,
    letterSpacing: "-0.025em",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: 44,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 14,
    paddingRight: 14,
    borderRadius: 11,
    border: `1px solid ${brand.lineStrong}`,
    background: isDarkMode ? brand.fill : brand.surface,
    color: brand.text,
    fontFamily: uiFont,
    fontSize: "0.95rem",
    fontWeight: 500,
    outline: "none",
    transition: "border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease",
    boxSizing: "border-box",
    boxShadow: "none",
  };

  // Select style with forced reset to remove Bootstrap/external carets
  const selectStyle: React.CSSProperties = {
    width: "100%",
    height: 44,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 14,
    paddingRight: 42,
    borderRadius: 11,
    border: `1px solid ${brand.lineStrong}`,
    background: isDarkMode ? brand.fill : brand.surface,
    color: brand.text,
    fontFamily: uiFont,
    colorScheme: isDarkMode ? "dark" : "light",
    fontSize: "0.95rem",
    fontWeight: 500,
    outline: "none",
    cursor: "pointer",
    // Force reset to remove any external (Bootstrap) arrows
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    boxShadow: "none",
    transition: "border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease",
    boxSizing: "border-box",
  };

  const RenderSegmentedControl = ({
    options,
    value,
    onChange,
    disabled = false,
  }: {
    options: { label: string; value: string; icon?: any; color?: string }[];
    value: string;
    onChange?: (val: string) => void;
    disabled?: boolean;
  }) => (
    <div style={{ display: "flex", gap: 4, background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", padding: 4, borderRadius: MediasfuBorders.md, opacity: disabled ? 0.5 : 1, boxShadow: isDarkMode ? "inset 0 1px 3px rgba(0,0,0,0.15)" : "inset 0 1px 3px rgba(0,0,0,0.04)", border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
      {options.map((opt) => {
        const isActive = value === opt.value;
        let activeBg = isDarkMode ? "rgba(255,255,255,0.1)" : "#fff";
        let activeColor = isDarkMode ? "#fff" : "#000";
        let borderColor = "transparent";

        if (isActive) {
          // Tinted, not filled: the accent at low alpha with readable text.
          const tint = (rgb: string, dark: string, light: string) => {
            activeBg = `rgba(${rgb},${isDarkMode ? 0.16 : 0.12})`;
            activeColor = isDarkMode ? dark : light;
            borderColor = `rgba(${rgb},0.34)`;
          };
          if (opt.value === "allow" || opt.value === "true") {
            tint("52,211,153", "#6ee7b7", "#047857");
          } else if (opt.value === "disallow" || opt.value === "false") {
            tint("251,113,133", "#fda4af", "#be123c");
          } else if (opt.value === "approval") {
            tint("251,191,36", "#fcd34d", "#b45309");
          } else {
            tint("45,212,191", MEDIASFU_BRAND.tealLight, MEDIASFU_BRAND.tealText);
          }
        }

        return (
          <button
            key={opt.value}
            onClick={() => !disabled && onChange?.(opt.value)}
            disabled={disabled}
            style={{
              flex: 1,
              padding: "6px 8px",
              borderRadius: MediasfuBorders.sm,
              border: `1px solid ${borderColor}`,
              background: isActive ? activeBg : "transparent",
              color: isActive ? activeColor : brand.muted,
              cursor: disabled ? "not-allowed" : "pointer",
              fontSize: "0.8rem",
              fontWeight: 600,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              transition: "all 0.2s ease",
            }}
            title={opt.label}
          >
            {opt.icon && <FontAwesomeIcon icon={opt.icon} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderTooltip = (message: string, content: React.ReactNode) => (
    <ModernTooltip message={message} position="top" isDarkMode={isDarkMode}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: MediasfuSpacing.xs }}>{content}</span>
    </ModernTooltip>
  );

  const labelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: MediasfuSpacing.xs,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: brand.soft,
    marginBottom: 6,
  };

  const mutedTextStyle: React.CSSProperties = {
    fontSize: "0.88rem",
    lineHeight: 1.55,
    color: brand.muted,
    margin: 0,
  };

  // Collapses with a 0fr -> 1fr grid row rather than a fixed max-height, which
  // cut every hint longer than two lines off mid-word.
  const tipShellStyle = (open: boolean): React.CSSProperties => ({
    display: "grid",
    gridTemplateRows: open ? "1fr" : "0fr",
    opacity: open ? 1 : 0,
    transition: "grid-template-rows 0.25s ease-in-out, opacity 0.2s ease",
  });
  const tipInnerStyle: React.CSSProperties = { overflow: "hidden", minHeight: 0 };

  const renderTip = (content: string, extraStyle?: React.CSSProperties) => (
    <div style={tipShellStyle(showTips)} aria-hidden={!showTips}>
      <div style={tipInnerStyle}>
        <p style={{ ...mutedTextStyle, marginTop: 4, ...extraStyle }}>
          <em>{content}</em>
        </p>
      </div>
    </div>
  );

  /**
   * Joining is a two-step flow (Meeting ID, then your details) but nothing on
   * screen said so. This renders a compact "step n of 2" marker above the
   * heading so people know how much is left.
   */
  const renderStepEyebrow = (step: 1 | 2) => {
    const labels = ["Meeting ID", "Your details"];
    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.sm, marginBottom: MediasfuSpacing.sm }}
        aria-label={`Step ${step} of 2: ${labels[step - 1]}`}
      >
        <span
          style={{
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: accentText,
            whiteSpace: "nowrap",
          }}
        >
          Step {step} of 2
        </span>
        <span aria-hidden="true" style={{ display: "flex", gap: 4, flex: 1, maxWidth: 96 }}>
          {[1, 2].map((s) => (
            <span
              key={s}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 999,
                background:
                  s <= step
                    ? MEDIASFU_BRAND.teal
                    : brand.lineStrong,
                transition: "background 0.25s ease",
              }}
            />
          ))}
        </span>
      </div>
    );
  };

  const pillToggleStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: MediasfuSpacing.xs,
    padding: `${MediasfuSpacing.sm}px ${MediasfuSpacing.md}px`,
    borderRadius: 10,
    // Segmented tab: the current one is a raised surface with teal text and a
    // hairline. The gradient is kept for the primary action below.
    // The raised surface is the sliding .mms-tab-thumb behind the buttons.
    position: "relative",
    zIndex: 1,
    border: "1px solid transparent",
    background: "transparent",
    color: active ? (isDarkMode ? MEDIASFU_BRAND.tealLight : MEDIASFU_BRAND.tealText) : brand.muted,
    fontFamily: uiFont,
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
    boxShadow: "none",
    transition: "color 0.2s ease",
  });

  const tabShellStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    gap: 4,
    marginBottom: MediasfuSpacing.lg,
    padding: 4,
    borderRadius: 13,
    background: isDarkMode ? "rgba(255,255,255,0.025)" : "rgba(15,23,42,0.035)",
    border: `1px solid ${brand.line}`,
  };


  const dividerStyle: React.CSSProperties = {
    height: 1,
    width: "100%",
    background: brand.line,
    margin: `${MediasfuSpacing.md}px 0`,
  };



  const tooltipInputStyle: React.CSSProperties = {
    ...inputStyle,
    position: "relative" as const,
  };

  const [activePanel, setActivePanel] = useState<"join" | "start">("join");
  const [showMediaSettingsPanel, setShowMediaSettingsPanel] = useState(false);
  const [audioTestState, setAudioTestState] = useState<"idle" | "recording" | "playing">("idle");

  // Collapsible sections state
  const [showCameraSection, setShowCameraSection] = useState(true);
  const [showAudioSection, setShowAudioSection] = useState(false);


  // Media preview card style (compact right sidebar)



  const compactSelectorGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: MediasfuSpacing.sm,
    opacity: showMediaSettingsPanel ? 0 : 1,
    maxHeight: showMediaSettingsPanel ? 0 : 220,
    overflow: "hidden",
    transition: "all 0.3s ease",
  };

  return (
    <>
      {/* Scoped CSS reset to remove Bootstrap/external select carets and theme options */}
      <style>{`
        .modern-meeting-setup select {
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
        }
        .modern-meeting-setup select::-ms-expand {
          display: none !important;
        }
        .modern-meeting-setup select option {
          background: ${brand.popover};
          color: ${brand.text};
          padding: 8px 12px;
        }
        .modern-meeting-setup select option:hover,
        .modern-meeting-setup select option:checked {
          background: ${brand.raised};
        }
        .modern-meeting-setup h1,
        .modern-meeting-setup h2,
        .modern-meeting-setup h3 {
          font-family: ${displayFont};
        }

        .modern-meeting-setup .cards-grid {
          grid-template-columns: 1fr;
          gap: 14px;
          width: calc(100% - 24px);
          max-width: calc(100% - 24px);
          margin: 0 auto;
          box-sizing: border-box;
          flex: 0 0 auto;
        }

        .modern-meeting-setup .brand-title {
          font-size: 1rem;
        }
        .modern-meeting-setup .brand-subtitle {
          font-size: 0.65rem;
        }
        .modern-meeting-setup .brand-logo {
          width: 32px;
          height: 32px;
        }
        .modern-meeting-setup .header-buttons {
          gap: 2px;
        }
        .modern-meeting-setup .header-btn-text {
          display: none;
        }
        .modern-meeting-setup .header-btn {
          padding: 6px 8px;
          height: 36px;
          transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
        }
        .modern-meeting-setup .header-btn:hover {
          transform: translateY(-1px);
          background: ${brand.fillHover} !important;
          color: ${brand.text} !important;
        }
        .modern-meeting-setup .brand-kicker {
          display: none;
        }
        .modern-meeting-setup .intelligence-spotlight {
          position: relative;
          display: grid;
          grid-template-columns: 1fr;
          gap: 10px;
          overflow: hidden;
          isolation: isolate;
          flex: 0 0 auto !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
          align-self: center;
        }
        .modern-meeting-setup .intelligence-spotlight::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          /* Sheen plus a faint starfield so the bar reads as a surface rather
             than a flat block of navy. */
          /* A faint signal grid: the "network" under every MediaSFU room.
             Static, so it costs nothing and never distracts. */
          background:
            linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px) 0 0 / 22px 22px,
            linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px) 0 0 / 22px 22px;
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 55%, #000 100%);
          mask-image: linear-gradient(90deg, transparent 0%, #000 55%, #000 100%);
          z-index: -1;
          pointer-events: none;
        }
        .modern-meeting-setup .intelligence-spotlight::after {
          content: "";
          position: absolute;
          width: 180px;
          height: 180px;
          right: -80px;
          top: -110px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(20,184,166,0.22) 0%, rgba(56,189,248,0.08) 48%, transparent 72%);
          z-index: -1;
          pointer-events: none;
        }
        .modern-meeting-setup .intelligence-features {
          /* On narrow screens the headline already carries the message; the
             chips would wrap to two rows and push the join card off-screen. */
          display: none;
          flex-wrap: wrap;
          align-content: center;
          gap: 6px;
        }
        @media (min-width: 640px) {
          .modern-meeting-setup .intelligence-features {
            display: flex;
          }
        }
        .modern-meeting-setup .intelligence-feature-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .modern-meeting-setup .intelligence-feature {
          transition: transform 180ms ease, background 180ms ease, border-color 180ms ease;
        }
        .modern-meeting-setup .intelligence-feature:hover {
          transform: translateY(-1px);
          background: rgba(45,212,191,0.10) !important;
          border-color: rgba(45,212,191,0.30) !important;
        }
        .modern-meeting-setup .entry-assurance {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px 14px;
        }
        .modern-meeting-setup .main-wrapper {
          padding: 16px 12px !important;
          box-sizing: border-box;
          width: 100%;
          max-width: 100%;
          min-height: 100% !important;
          height: auto !important;
          flex: 0 0 auto !important;
        }
        .modern-meeting-setup .glass-panel {
          padding: 16px !important;
          max-width: 100% !important;
          width: 100% !important;
          overflow-x: hidden;
          box-sizing: border-box;
          transition: border-color 160ms ease;
        }
        .modern-meeting-setup .glass-panel:hover {
          border-color: ${brand.lineStrong};
        }
        .modern-meeting-setup .cards-grid > * {
          max-width: 100% !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        .modern-meeting-setup .form-grid {
          grid-template-columns: 1fr !important;
        }
        .modern-meeting-setup input,
        .modern-meeting-setup select,
        .modern-meeting-setup button {
          max-width: 100%;
          box-sizing: border-box;
        }
        .modern-meeting-setup input:hover,
        .modern-meeting-setup select:hover {
          border-color: ${isDarkMode ? "rgba(148,163,184,0.32)" : "rgba(15,23,42,0.22)"} !important;
        }
        .modern-meeting-setup input::placeholder {
          color: ${brand.muted};
          opacity: 1;
        }

        @media (min-width: 500px) {
          .modern-meeting-setup .form-grid:not(.is-quick) {
            grid-template-columns: 1fr 1fr !important;
          }
        }

        @media (min-width: 400px) {
          .modern-meeting-setup .brand-title {
            font-size: 1.15rem;
          }
          .modern-meeting-setup .brand-subtitle {
            font-size: 0.72rem;
          }
          .modern-meeting-setup .brand-logo {
            width: 40px;
            height: 40px;
          }
          .modern-meeting-setup .header-buttons {
            gap: 4px;
          }
          .modern-meeting-setup .header-btn {
            padding: 8px 12px;
            height: 44px;
          }
          .modern-meeting-setup .main-wrapper {
            padding-left: 16px;
            padding-right: 16px;
          }
          .modern-meeting-setup .glass-panel {
            padding: 22px !important;
          }
        }

        @media (min-width: 600px) {
          .modern-meeting-setup .brand-title {
            font-size: 1.5rem;
          }
          .modern-meeting-setup .brand-subtitle {
            font-size: 0.85rem;
          }
          .modern-meeting-setup .brand-logo {
            width: 52px;
            height: 52px;
          }
          .modern-meeting-setup .header-buttons {
            gap: ${MediasfuSpacing.xs}px;
          }
          .modern-meeting-setup .header-btn-text {
            display: inline;
          }
          .modern-meeting-setup .brand-kicker {
            display: inline-flex;
          }
        }

        @media (min-width: 900px) {
          .modern-meeting-setup .cards-grid {
            grid-template-columns: minmax(0, 1fr) minmax(320px, 390px);
          }
          .modern-meeting-setup .intelligence-spotlight {
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: center;
            min-height: 0 !important;
          }
          .modern-meeting-setup .intelligence-features {
            flex-wrap: nowrap;
            justify-content: flex-end;
          }
        }

        @media (min-width: 900px) and (min-height: 760px) {
          .modern-meeting-setup .main-wrapper.join-layout {
            justify-content: safe center;
            padding-top: clamp(24px, 4vh, 44px) !important;
            padding-bottom: clamp(48px, 8vh, 88px) !important;
          }
        }

        /* ── Green room layout ─────────────────────────────────────────── */
        .modern-meeting-setup .main-wrapper { position: relative; z-index: 1; }
        .modern-meeting-setup .mms-header {
          width: 100%; max-width: 1200px; margin: 0 auto 6px; padding: 4px 2px;
          display: flex; align-items: center; justify-content: space-between; gap: 12px; box-sizing: border-box;
        }
        .modern-meeting-setup .mms-brand { display: flex; align-items: center; gap: 10px; min-width: 0; color: inherit; text-decoration: none; }
        .modern-meeting-setup .mms-brand__logo { width: 34px; height: 34px; border-radius: 50%; border: 1px solid ${brand.lineStrong}; flex-shrink: 0; object-fit: cover; }
        .modern-meeting-setup .mms-brand__name { font: 700 1.15rem/1 ${displayFont}; letter-spacing: -0.02em; color: ${brand.text}; white-space: nowrap; }
        .modern-meeting-setup .mms-brand__kicker {
          font-size: 0.62rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: ${accentText};
          padding: 4px 8px; border-radius: 999px; background: rgba(45,212,191,0.08); border: 1px solid rgba(45,212,191,0.26);
        }
        .modern-meeting-setup .mms-header__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
        .modern-meeting-setup .mms-chip-btn,
        .modern-meeting-setup .mms-icon-btn {
          height: 36px; border-radius: 999px; border: 1px solid ${brand.line}; background: ${brand.fill}; color: ${brand.muted};
          display: inline-flex; align-items: center; justify-content: center; gap: 7px; cursor: pointer;
          font: 600 0.8rem/1 ${uiFont}; transition: background 0.16s ease, color 0.16s ease, border-color 0.16s ease;
        }
        .modern-meeting-setup .mms-chip-btn { padding: 0 13px; }
        .modern-meeting-setup .mms-icon-btn { width: 36px; padding: 0; }
        .modern-meeting-setup .mms-chip-btn:hover,
        .modern-meeting-setup .mms-icon-btn:hover { background: ${brand.fillHover}; color: ${brand.text}; }
        .modern-meeting-setup .mms-chip-btn.is-on { color: ${accentText}; border-color: rgba(45,212,191,0.30); background: rgba(45,212,191,0.08); }

        .modern-meeting-setup .mms-grid {
          width: 100%; max-width: 1200px; margin: 6px auto 0; box-sizing: border-box;
          display: grid; gap: 18px; grid-template-columns: minmax(0, 1fr); grid-template-areas: "panel" "stage";
        }
        .modern-meeting-setup .mms-panel { grid-area: panel; min-width: 0; }
        .modern-meeting-setup .mms-stage { grid-area: stage; min-width: 0; display: flex; flex-direction: column; gap: 12px; }

        .modern-meeting-setup .mms-hero { margin: 6px 0 18px; }
        .modern-meeting-setup .mms-eyebrow {
          display: inline-flex; align-items: center; gap: 8px; color: ${accentText};
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
        }
        .modern-meeting-setup .mms-eyebrow__dot { width: 7px; height: 7px; border-radius: 50%; background: #34D399; box-shadow: 0 0 0 4px rgba(52,211,153,0.16); }
        .modern-meeting-setup .mms-headline {
          margin: 12px 0 10px; color: ${brand.text}; font-family: ${displayFont}; font-weight: 700;
          font-size: clamp(2.1rem, 7.6vw, 3.25rem); line-height: 1.02; letter-spacing: -0.035em;
        }
        .modern-meeting-setup .mms-headline__accent { color: ${accentText}; }
        .modern-meeting-setup .mms-lede { margin: 0; max-width: 470px; color: ${brand.soft}; font-size: 0.98rem; line-height: 1.6; }
        .modern-meeting-setup .mms-capabilities { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 16px; }
        .modern-meeting-setup .mms-capability {
          display: inline-flex; align-items: center; gap: 7px; height: 28px; padding: 0 11px; border-radius: 999px;
          background: ${brand.fill}; border: 1px solid ${brand.line}; color: ${brand.soft};
          font-size: 0.74rem; font-weight: 600; text-decoration: none; white-space: nowrap;
        }
        .modern-meeting-setup .mms-capability svg { color: ${accentText}; font-size: 0.7rem; }
        .modern-meeting-setup .mms-capability--link { transition: border-color 0.16s ease, color 0.16s ease; }
        .modern-meeting-setup .mms-capability--link:hover { color: ${brand.text}; border-color: rgba(45,212,191,0.35); }
        .modern-meeting-setup .mms-capabilities__dismiss {
          width: 28px; height: 28px; border-radius: 999px; border: none; background: transparent; color: ${brand.muted}; cursor: pointer;
        }
        .modern-meeting-setup .mms-capabilities__dismiss:hover { color: ${brand.text}; background: ${brand.fill}; }

        .modern-meeting-setup .mms-flow {
          background: ${isDarkMode ? "rgba(11,18,32,0.82)" : "rgba(255,255,255,0.88)"};
          border: 1px solid ${brand.line}; border-radius: 20px; padding: 18px; box-sizing: border-box;
          box-shadow: ${isDarkMode ? "0 30px 60px -40px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 30px 60px -40px rgba(15,23,42,0.4)"};
        }

        .modern-meeting-setup .mms-stage__frame {
          position: relative; width: 100%; aspect-ratio: 16 / 10; border-radius: 24px; overflow: hidden; isolation: isolate;
          background: radial-gradient(120% 90% at 50% 35%, #111B2E 0%, #060A13 70%);
          border: 1px solid ${isDarkMode ? brand.line : "rgba(15,23,42,0.12)"};
          box-shadow: 0 40px 80px -46px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .modern-meeting-setup .mms-stage__video { width: 100%; height: 100%; object-fit: cover; display: block; transform: scaleX(-1); }
        .modern-meeting-setup .mms-stage__enable { position: absolute; inset: 0; padding: 0; border: 0; background: transparent; cursor: pointer; color: inherit; }
        .modern-meeting-setup .mms-stage__enable:focus-visible { outline: 2px solid #2DD4BF; outline-offset: -3px; border-radius: 24px; }
        .modern-meeting-setup .mms-stage__cta {
          position: absolute; left: 50%; top: calc(50% + 62px); transform: translateX(-50%);
          display: inline-flex; align-items: center; gap: 8px; height: 38px; padding: 0 16px; border-radius: 999px;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.16); color: #E8EEF6;
          font: 600 0.86rem/1 ${uiFont}; white-space: nowrap; transition: background 0.16s ease, border-color 0.16s ease;
        }
        .modern-meeting-setup .mms-stage__enable:hover .mms-stage__cta { background: rgba(45,212,191,0.16); border-color: rgba(45,212,191,0.45); }
        .modern-meeting-setup .mms-stage__top,
        .modern-meeting-setup .mms-stage__bottom {
          position: absolute; left: 14px; right: 14px; display: flex; justify-content: space-between; align-items: center;
          gap: 8px; pointer-events: none; z-index: 2;
        }
        .modern-meeting-setup .mms-stage__top { top: 14px; }
        .modern-meeting-setup .mms-stage__bottom { bottom: 14px; }
        .modern-meeting-setup .mms-glass-pill {
          display: inline-flex; align-items: center; gap: 7px; height: 30px; padding: 0 12px; border-radius: 999px;
          background: rgba(6,10,19,0.62); border: 1px solid rgba(255,255,255,0.10); color: #E8EEF6;
          font: 600 0.74rem/1 ${uiFont}; max-width: 62%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .modern-meeting-setup .mms-status__dot { width: 7px; height: 7px; border-radius: 50%; background: #8394AA; flex-shrink: 0; }
        .modern-meeting-setup .mms-status.is-live .mms-status__dot { background: #34D399; box-shadow: 0 0 0 3px rgba(52,211,153,0.24); }
        .modern-meeting-setup .mms-mic svg { color: #5EEAD4; font-size: 0.72rem; }
        .modern-meeting-setup .mms-stage__loading { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(6,10,19,0.5); z-index: 1; }
        .modern-meeting-setup .mms-stage__hint { margin: 0; padding: 0 4px; color: ${brand.muted}; font-size: 0.8rem; line-height: 1.5; }

        @media (max-width: 420px) {
          .modern-meeting-setup .mms-chip-btn__text,
          .modern-meeting-setup .mms-brand__kicker { display: none; }
          .modern-meeting-setup .mms-chip-btn { width: 36px; padding: 0; }
          .modern-meeting-setup .mms-flow { padding: 14px; border-radius: 18px; }
          .modern-meeting-setup .mms-stage__frame { border-radius: 20px; }
          .modern-meeting-setup .mms-stage__frame { aspect-ratio: 4 / 3; }
          .modern-meeting-setup .mms-orbit__core { width: 64px; height: 64px; font-size: 24px; }
          .modern-meeting-setup .mms-stage__cta { top: calc(50% + 42px); height: 34px; padding: 0 14px; font-size: 0.8rem; }
          .modern-meeting-setup .mms-appstrip > div[aria-hidden="true"],
          .modern-meeting-setup .mms-appstrip p { display: none !important; }
        }
        @media (min-width: 980px) {
          .modern-meeting-setup .mms-grid {
            /* Even split: the form is where the work happens, and the preview stays sticky beside it. */
            grid-template-columns: minmax(0, 1fr) minmax(440px, 1fr); grid-template-areas: "stage panel";
            gap: 30px; align-items: start; margin-top: 14px;
          }
          .modern-meeting-setup .mms-stage { position: sticky; top: 18px; }
          .modern-meeting-setup .mms-hero { margin-top: 2px; }
        }

        .modern-meeting-setup .loader-spin {
          position: absolute;
          inset: 0;
          border: 3px solid ${brand.line};
          border-top-color: ${MEDIASFU_BRAND.teal};
          border-radius: 50%;
          animation: modern-spin 1s linear infinite;
        }

        .modern-meeting-setup input:focus,
        .modern-meeting-setup select:focus {
          border-color: rgba(45,212,191,0.5) !important;
          background: ${isDarkMode ? "rgba(45,212,191,0.04)" : "#ffffff"} !important;
          box-shadow: 0 0 0 3px rgba(45,212,191,${isDarkMode ? 0.12 : 0.14}) !important;
        }

        /* Join / Start thumb: one raised surface that glides to the chosen side. */
        .modern-meeting-setup .mms-tab-thumb {
          position: absolute; top: 4px; bottom: 4px; left: 4px; width: calc(50% - 6px); z-index: 0;
          border-radius: 10px; border: 1px solid ${brand.lineStrong};
          background: ${isDarkMode ? brand.raised : brand.surface};
          box-shadow: ${isDarkMode ? "0 8px 18px -12px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05)" : "0 6px 14px -10px rgba(15,23,42,0.35)"};
          transition: transform 0.38s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .modern-meeting-setup .mms-tab-thumb[data-side="start"] { transform: translateX(calc(100% + 4px)); }

        /* The field being edited names itself: its label takes the accent. */
        .modern-meeting-setup div:has(> input:focus, > select:focus, > textarea:focus) > label,
        .modern-meeting-setup div:has(> div > input:focus, > div > select:focus) > label {
          color: ${accentText} !important;
          transition: color 0.16s ease;
        }

        /* Create stays in reach: once the settings run past the fold, the action
           bar rides the bottom edge on frosted glass. */
        .modern-meeting-setup .mms-actionbar {
          position: sticky; bottom: 12px; z-index: 5; align-items: center;
          margin-left: -8px !important; margin-right: -8px !important; padding: 10px;
          border-radius: 16px; border: 1px solid ${brand.line};
          background: ${isDarkMode ? "rgba(11,18,32,0.94)" : "rgba(255,255,255,0.94)"};
          -webkit-backdrop-filter: blur(14px) saturate(1.2); backdrop-filter: blur(14px) saturate(1.2);
          box-shadow: ${isDarkMode ? "0 -18px 36px -24px rgba(0,0,0,0.9)" : "0 -16px 30px -24px rgba(15,23,42,0.35)"};
        }

        .modern-meeting-setup button:focus-visible,
        .modern-meeting-setup a:focus-visible {
          outline: 2px solid ${accentText};
          outline-offset: 2px;
          border-radius: ${MediasfuBorders.sm}px;
        }

        @media (prefers-reduced-motion: reduce) {
          .modern-meeting-setup *,
          .modern-meeting-setup *::before,
          .modern-meeting-setup *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        @keyframes modern-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={containerStyle} className={`modern-meeting-setup ${isDarkMode ? "mms-dark" : "mms-light"}`}>
      <SetupVisualStyles />
      <AuroraBackdrop isDarkMode={isDarkMode} />
      {loading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(6px)",
            background: isDarkMode ? "rgba(6,10,19,0.7)" : "rgba(243,246,250,0.7)",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              padding: MediasfuSpacing.xl,
              background: brand.popover,
              borderRadius: 18,
              boxShadow: "0 32px 64px -24px rgba(0,0,0,0.7)",
              border: `1px solid ${brand.line}`,
              minWidth: 260,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: MediasfuSpacing.sm }}>
              <div style={{ position: "relative", width: 64, height: 64 }}>
                <div className="loader-spin" />
              </div>
              <p style={{ ...mutedTextStyle, margin: 0, textAlign: "center" }}>Setting things up…</p>
            </div>
          </div>
        </div>
      )}

      <div style={mainWrapperStyle} className={`main-wrapper ${activePanel === "join" ? "join-layout" : "start-layout"}`}>
        {/* Header: the brand, quietly, and two small controls. */}
        <header className="mms-header">
          <a className="mms-brand" href="https://mediasfu.com" target="_blank" rel="noopener noreferrer">
            <img src="https://mediasfu.com/images/logo192.png" alt="" className="mms-brand__logo" />
            <span className="mms-brand__name">MediaSFU</span>
            <span className="mms-brand__kicker">Meetings</span>
          </a>
          <div className="mms-header__actions">
            <button
              type="button"
              className={`mms-chip-btn${showTips ? " is-on" : ""}`}
              onClick={() => {
                const newVal = !showTips;
                setShowTips(newVal);
                showAlert(newVal ? "Tips are now visible" : "Tips are now hidden", "success", 2000);
              }}
              aria-pressed={showTips}
              title="Toggle contextual tips"
            >
              <FontAwesomeIcon icon={showTips ? faLightbulb : faQuestionCircle} />
              <span className="mms-chip-btn__text">{showTips ? "Tips on" : "Tips off"}</span>
            </button>
            <button
              type="button"
              className="mms-icon-btn"
              onClick={() => setIsDarkMode(!isDarkMode)}
              aria-label={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
              title={isDarkMode ? "Light theme" : "Dark theme"}
            >
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
            </button>
          </div>
        </header>
        {/* Alert Banner */}
        {alertVisible && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2500,
              maxWidth: 520,
              width: "calc(100% - 32px)",
              padding: `${MediasfuSpacing.sm + 2}px ${MediasfuSpacing.md}px`,
              borderRadius: MediasfuBorders.lg,
              background:
                alertType === "success"
                  ? (isDarkMode ? "rgba(16,185,129,0.95)" : "rgba(220,252,231,0.98)")
                  : alertType === "danger"
                  ? (isDarkMode ? "rgba(185,28,28,0.95)" : "rgba(254,226,226,0.98)")
                  : alertType === "warning"
                  ? (isDarkMode ? "rgba(161,98,7,0.95)" : "rgba(254,249,195,0.98)")
                  : (isDarkMode ? "rgba(30,64,175,0.95)" : "rgba(219,234,254,0.98)"),
              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              border: `1px solid ${alertType === "success" ? "rgba(16,185,129,0.6)" : alertType === "danger" ? "rgba(239,68,68,0.6)" : alertType === "warning" ? "rgba(234,179,8,0.6)" : "rgba(59,130,246,0.6)"}`,
              boxShadow: "0 14px 36px rgba(0,0,0,0.35)",
              backdropFilter: "blur(8px)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: MediasfuSpacing.md }}>
              <span style={{ fontWeight: 600, textAlign: "center", flex: 1 }}>{alertMessage}</span>
              <button
                onClick={() => setAlertVisible(false)}
                style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 4, flexShrink: 0 }}
                aria-label="Close alert"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        {/* Green room: command panel (what you do) beside the stage (how you look). */}
        <div className="mms-grid">
          <section className="mms-panel" aria-label={activePanel === "join" ? "Join a meeting" : "Start a meeting"}>
            <div className="mms-hero">
              <span className="mms-eyebrow"><span className="mms-eyebrow__dot" aria-hidden="true" />MediaSFU Meetings</span>
              <h1 className="mms-headline">
                {activePanel === "join" ? <>Ready when <span className="mms-headline__accent">you are.</span></> : <>Your room, <span className="mms-headline__accent">in seconds.</span></>}
              </h1>
              <p className="mms-lede">
                {activePanel === "join"
                  ? "Join with the meeting ID your host shared. Check your camera and mic first, then walk straight in."
                  : "Give it a name and a few essentials. We create the room and you are live."}
              </p>
              {showTranslationBanner && (
                <div className="mms-capabilities" aria-label="Included in every meeting">
                  {[
                    { icon: faLanguage, label: "Live translation", detail: "50+ languages" },
                    { icon: faClipboardCheck, label: "Transcripts", detail: "Searchable record" },
                    { icon: faWandMagicSparkles, label: "AI notes", detail: "Key moments" },
                    { icon: faBolt, label: "Smart summaries", detail: "Clear next steps" },
                  ].map((feature) => (
                    <span key={feature.label} className="mms-capability" title={`${feature.label}: ${feature.detail}`}>
                      <FontAwesomeIcon icon={feature.icon} />
                      {feature.label}
                    </span>
                  ))}
                  <a className="mms-capability mms-capability--link" href="https://mediasfu.com/lite/voice-clone" target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faBrain} />
                    Voice cloning
                  </a>
                  <button
                    type="button"
                    className="mms-capabilities__dismiss"
                    onClick={() => {
                      setShowTranslationBanner(false);
                      localStorage.setItem('mediasfu_translation_banner_dismissed', 'true');
                    }}
                    aria-label="Hide these highlights"
                    title="Hide"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>
            <div className="mms-flow">
            <div style={tabShellStyle} role="tablist">
              <span className="mms-tab-thumb" data-side={activePanel} aria-hidden="true" />
              <button
                role="tab"
                aria-selected={activePanel === "join"}
                style={pillToggleStyle(activePanel === "join")}
                onClick={() => {
                  if (activePanel === "join") return;
                  setActivePanel("join");
                  setCurrentStep(1);
                  setShowStartMeetingPanel(false);
                  setShowMediaSettings(false);
                }}
              >
                <FontAwesomeIcon icon={faUsers} /> Join meeting
              </button>
              <button
                role="tab"
                aria-selected={activePanel === "start"}
                style={pillToggleStyle(activePanel === "start")}
                onClick={() => {
                  if (activePanel === "start") return;
                  setActivePanel("start");
                  setShowStartMeetingPanel(true);
                  setShowMediaSettings(false);
                  resetProcess();
                }}
              >
                <FontAwesomeIcon icon={faVideo} /> Start meeting
              </button>
            </div>

            {activePanel === "join" && (
              <div className="mms-swap" key="join">
                {currentStep === 1 && (
                  <>
                    {renderStepEyebrow(1)}
                    <h3 style={sectionTitleStyle}>
                      <FontAwesomeIcon icon={faUsers} style={{ color: MediasfuColors.primary }} /> Join your meeting
                    </h3>
                    <p style={mutedTextStyle}>Use the Meeting ID shared by your host.</p>
                    <div style={{ marginTop: MediasfuSpacing.md }}>
                      <label style={labelStyle} htmlFor="meetingIDInput">
                        <FontAwesomeIcon icon={faKeyboard} /> Meeting ID
                      </label>
                      <input
                        id="meetingIDInput"
                        type="text"
                        value={meetingID}
                        onChange={(e) => setMeetingID(editMeetingID(e.target.value))}
                        placeholder="Enter your meeting ID"
                        style={inputStyle}
                        title="Meeting ID provided by host (alphanumeric)"
                        autoComplete="off"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") handleConfirmMeetingIDClick(); }}
                        aria-invalid={!!warningMessage}
                        aria-describedby={warningMessage ? "meetingIDWarning" : undefined}
                      />
                    </div>
                    {warningMessage && (
                      <div id="meetingIDWarning" role="alert" style={{ color: isDarkMode ? "#fca5a5" : "#b91c1c", marginTop: MediasfuSpacing.sm, fontSize: "0.9rem", fontWeight: 600 }}>{warningMessage}</div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.md, marginTop: MediasfuSpacing.lg, flexWrap: "wrap" }}>
                      <PremiumButton
                        variant="gradient" gradient={MEDIASFU_BRAND.gradient} textColor={MEDIASFU_BRAND.ink}
                        size="lg"
                        onPress={handleConfirmMeetingIDClick}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        isDarkMode={isDarkMode}
                      >
                        Continue
                      </PremiumButton>
                      <PremiumButton
                        variant="ghost" textColor={accentText}
                        size="lg"
                        onPress={() => {
                          setActivePanel("start");
                          setShowStartMeetingPanel(true);
                          resetProcess();
                        }}
                        isDarkMode={isDarkMode}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Need a room? Create
                      </PremiumButton>
                    </div>
                    <div className="entry-assurance" style={{ ...mutedTextStyle, marginTop: MediasfuSpacing.md, fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#52627a" }}>
                      <span><FontAwesomeIcon icon={faBolt} style={{ color: MediasfuColors.primary, marginRight: 5 }} />Browser-based</span>
                      <span><FontAwesomeIcon icon={faVideo} style={{ color: MediasfuColors.primary, marginRight: 5 }} />Device preview</span>
                      <span><FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: MediasfuColors.primary, marginRight: 5 }} />AI-ready</span>
                    </div>
                  </>
                )}

                {currentStep === 2 && (
                  <>
                    {renderStepEyebrow(2)}
                    <h3 style={sectionTitleStyle}>
                      <FontAwesomeIcon icon={faUser} style={{ color: MediasfuColors.primary }} /> Your details
                    </h3>
                    <p style={mutedTextStyle}>
                      Joining <strong style={{ color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>{meetingID}</strong> — this name is visible to others.
                    </p>
                    <div style={{ marginTop: MediasfuSpacing.md }}>
                      <label style={labelStyle} htmlFor="userNameInputMain">
                        <FontAwesomeIcon icon={faUser} /> Display name
                      </label>
                      <input
                        id="userNameInputMain"
                        ref={userNameInputMainRef}
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(editText(e.target.value))}
                        placeholder="Your name"
                        style={inputStyle}
                        title="Display name shown to participants (2-10 characters)"
                        autoComplete="nickname"
                        onKeyDown={(e) => { if (e.key === "Enter" && userName.trim() && !codeVisible) handleJoinMeetingClick(); }}
                        aria-invalid={!!nameWarn}
                        aria-describedby={nameWarn ? "userNameWarning" : "userNameHint"}
                      />
                      <div id="userNameHint" style={{ marginTop: MediasfuSpacing.xs, fontSize: "0.8rem", color: isDarkMode ? "#94a3b8" : "#52627a" }}>
                        <p style={{ margin: 0 }}>2–10 letters or numbers, no spaces or symbols.</p>
                        {showTips && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: `2px ${MediasfuSpacing.md}px`, marginTop: 4 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: isDarkMode ? "#6ee7b7" : "#047857" }}>
                              <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />
                              <span>John, Doe123, Alex</span>
                            </span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: isDarkMode ? "#fca5a5" : "#b91c1c" }}>
                              <FontAwesomeIcon icon={faTimes} style={{ fontSize: 12 }} />
                              <span>J, User_Name, LongName1234</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {nameWarn && (
                      <div id="userNameWarning" role="alert" style={{ color: isDarkMode ? "#fca5a5" : "#b91c1c", marginTop: MediasfuSpacing.sm, fontSize: "0.9rem", fontWeight: 600 }}>{nameWarn}</div>
                    )}
                    {!codeVisible && (
                      <div style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.md, marginTop: MediasfuSpacing.lg, flexWrap: "wrap" }}>
                        <PremiumButton
                          variant="gradient" gradient={MEDIASFU_BRAND.gradient} textColor={MEDIASFU_BRAND.ink}
                          size="lg"
                          onPress={handleJoinMeetingClick}
                          disabled={!userName.trim()}
                          icon={<FontAwesomeIcon icon={faSignInAlt} />}
                          isDarkMode={isDarkMode}
                        >
                          Join meeting
                        </PremiumButton>
                        <PremiumButton
                          variant="ghost" textColor={accentText}
                          size="lg"
                          onPress={() => { setCurrentStep(1); setNameWarn(""); }}
                          isDarkMode={isDarkMode}
                          icon={<FontAwesomeIcon icon={faArrowLeft} />}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Back
                        </PremiumButton>
                      </div>
                    )}
                    {codeVisible && (
                      <div
                        style={{
                          display: "flex",
                          gap: MediasfuSpacing.lg,
                          alignItems: "center",
                          padding: MediasfuSpacing.lg,
                          background: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(45,212,191,0.07)",
                          borderRadius: MediasfuBorders.lg,
                          marginTop: MediasfuSpacing.lg,
                        }}
                      >
                        {allowScan && (
                          <QRCodeSVG value={`${userName};${urll};${userSecret};${secureCode};${meetingID}`} size={120} />
                        )}
                        <div>
                          <p style={{ fontWeight: 700, marginBottom: MediasfuSpacing.xs }}>Join on mobile or web</p>
                          <p style={{ margin: 0 }}>
                            1. <button onClick={() => cleanupAndNavigate(() => { window.location.href = urll; })} style={{ color: MediasfuColors.primary, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Open link</button>
                          </p>
                          {allowScan && (
                            <p style={{ margin: 0 }}>2. Or scan QR code.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activePanel === "start" && (
              <div className="mms-swap" key="start">
                <h3 style={sectionTitleStyle}>
                  <FontAwesomeIcon icon={faClipboardCheck} style={{ color: MediasfuColors.primary }} /> Create a meeting
                </h3>
                <p style={{ ...mutedTextStyle, display: "flex", alignItems: "center", gap: MediasfuSpacing.sm, flexWrap: "wrap" }}>
                  Configure essentials, then we generate the room.
                  {!showTips && (
                    <button
                      onClick={() => setShowTips(true)}
                      style={{
                        background: `${MediasfuColors.primary}15`,
                        border: `1px solid ${MediasfuColors.primary}40`,
                        borderRadius: 16,
                        cursor: "pointer",
                        color: MediasfuColors.primary,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        padding: "4px 10px",
                        marginLeft: 4,
                        transition: "all 0.2s ease",
                      }}
                      title="Show helpful tips"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${MediasfuColors.primary}25`;
                        e.currentTarget.style.borderColor = MediasfuColors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `${MediasfuColors.primary}15`;
                        e.currentTarget.style.borderColor = `${MediasfuColors.primary}40`;
                      }}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} /> Show tips
                    </button>
                  )}
                  <button
                    onClick={() => setQuickSetup(!quickSetup)}
                    style={{
                      background: quickSetup ? `${MediasfuColors.primary}20` : `${MediasfuColors.primary}10`,
                      border: `1px solid ${quickSetup ? MediasfuColors.primary : `${MediasfuColors.primary}40`}`,
                      borderRadius: 16,
                      cursor: "pointer",
                      color: MediasfuColors.primary,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      padding: "4px 10px",
                      marginLeft: 4,
                      transition: "all 0.2s ease",
                    }}
                    title={quickSetup ? "Click to show all settings" : "Click for quick setup mode"}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${MediasfuColors.primary}30`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = quickSetup ? `${MediasfuColors.primary}20` : `${MediasfuColors.primary}10`;
                    }}
                  >
                    <FontAwesomeIcon icon={faBolt} /> Quick Setup {quickSetup ? "On" : "Off"}
                  </button>
                </p>
                {quickSetup && (
                  <p style={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: 4, fontStyle: "italic" }}>
                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 4 }} />
                    Showing essential fields only. Click &quot;Quick Setup&quot; to configure more options.
                  </p>
                )}

                <div className={`form-grid${quickSetup ? " is-quick" : ""}`} style={{ display: "grid", gridTemplateColumns: quickSetup ? "1fr" : "1fr 1fr", gap: MediasfuSpacing.lg, marginTop: MediasfuSpacing.md }}>
                  <div>
                    <div style={{ marginTop: 0 }}>
                      <label style={labelStyle}>
                        <FontAwesomeIcon icon={faUser} /> Display name
                      </label>
                      <input
                        id="userNameInputModal"
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(editText(e.target.value))}
                        placeholder="Enter your name"
                        style={inputStyle}
                        title="Display name shown to participants (2-10 characters)"
                      />
                      {renderTip("Shown to participants; 2–10 letters/numbers.")}
                    </div>
                    {!quickSetup && (
                       <div style={{ marginTop: MediasfuSpacing.md }}>
                        <label style={labelStyle}>
                          <FontAwesomeIcon icon={faClock} /> Meeting Duration
                        </label>
                        <select
                          id="durationSelect"
                          value={selectedDurationMinutes}
                          onChange={(e) => setSelectedDurationMinutes(parseInt(e.target.value, 10))}
                          style={selectStyle}
                          title="Meeting duration in minutes"
                        >
                          {durationOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {renderTip("Pick how long the room stays open (minutes).")}

                        <div style={{ marginTop: MediasfuSpacing.md }}>
                          <label style={labelStyle}>
                            <FontAwesomeIcon icon={faUsers} /> Room Capacity
                          </label>
                          <input
                            type="number"
                            value={capacity?.toString() || ""}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                setCapacity("");
                              } else {
                                const val = parseInt(e.target.value, 10);
                                if (val > refRoomCapacity) {
                                  setCapacity(refRoomCapacity);
                                  showAlert(`Room capacity cannot exceed ${refRoomCapacity}. Set to max.`, "warning");
                                } else if (val < 2 && val > 0) {
                                  setCapacity(2);
                                  showAlert("Room capacity must be at least 2. Set to minimum.", "warning");
                                } else {
                                  setCapacity(val);
                                }
                              }
                            }}
                            placeholder="2"
                            style={inputStyle}
                            title="Maximum participants (positive integer)"
                          />
                          {renderTip(`Max people allowed (must be between 2 and ${refRoomCapacity}).`)}
                        </div>
                      </div>
                    )}

                    {!quickSetup && (
                      <div style={{ marginTop: MediasfuSpacing.md }}>
                        <label style={labelStyle}>
                          <FontAwesomeIcon icon={faCalendar} /> Date/Time
                        </label>
                        <input type="datetime-local" onChange={(e) => onDateChange(e.target.value)} style={inputStyle} />
                        <div style={{ fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: 4, fontStyle: "italic" }}>
                          {scheduledDateState
                            ? `Scheduled for: ${new Date(scheduledDateState).toLocaleString()} (Local) / ${new Date(scheduledDateState).toUTCString()} (GMT)`
                            : "Scheduled for: Instantly"}
                        </div>
                        {renderTip("Leave empty to start now, or pick a time within the next 90 days.")}
                      </div>
                    )}

                    {/* Event Type - show in same column when quick setup is on */}
                    {quickSetup && (
                      <div style={{ marginTop: MediasfuSpacing.md }}>
                        <label style={labelStyle}>
                          <FontAwesomeIcon icon={faVideo} /> Event Type
                        </label>
                        <select
                          value={actType}
                          onChange={(e) => {
                            const val = e.target.value as MeetingParams["type"];
                            setActType(val);
                            meetingParams.current.type = val;
                          }}
                          style={selectStyle}
                          title="Choose the type of event"
                        >
                          <option value="broadcast">Broadcast</option>
                          <option value="webinar">Webinar</option>
                          <option value="conference">Conference</option>
                          <option value="chat">Chat</option>
                        </select>
                        {renderTip(
                          actType === "broadcast"
                            ? "Highest capacity; only host produces; supports recording; no screenshare/whiteboard."
                            : actType === "webinar"
                            ? "Like conference but host stays on main stage; supports all features."
                            : actType === "conference"
                            ? "All participants equal; supports recording, screenshare, whiteboard, breakout rooms."
                            : "Private 1-on-1; max 2 people; no recording, screenshare, or whiteboard."
                        )}
                      </div>
                    )}
                  </div>

                  {!quickSetup && (
                  <div>
                    <label style={labelStyle}>
                      <FontAwesomeIcon icon={faVideo} /> Event Type
                    </label>
                    <select
                      value={actType}
                      onChange={(e) => {
                        const val = e.target.value as MeetingParams["type"];
                        setActType(val);
                        meetingParams.current.type = val;
                      }}
                      style={selectStyle}
                      title="Choose the type of event"
                    >
                      <option value="broadcast">Broadcast</option>
                      <option value="webinar">Webinar</option>
                      <option value="conference">Conference</option>
                      <option value="chat">Chat</option>
                    </select>
                    {renderTip(
                      actType === "broadcast"
                        ? "Highest capacity; only host produces; supports recording; no screenshare/whiteboard."
                        : actType === "webinar"
                        ? "Like conference but host stays on main stage; supports all features."
                        : actType === "conference"
                        ? "All participants equal; supports recording, screenshare, whiteboard, breakout rooms."
                        : "Private 1-on-1; max 2 people; no recording, screenshare, or whiteboard."
                    )}

                    <div style={{ marginTop: MediasfuSpacing.md, padding: MediasfuSpacing.sm, background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: MediasfuBorders.sm }}>
                      <p style={{ margin: 0, fontSize: "0.85rem", color: isDarkMode ? "#cbd5e1" : "#475569" }}>
                        <strong>Max Video Quality:</strong>{" "}
                        {meetingParams.current.targetResolutionHost === meetingParams.current.targetResolution
                          ? meetingParams.current.targetResolution.toUpperCase()
                          : `Host ${meetingParams.current.targetResolutionHost.toUpperCase()}, Others ${meetingParams.current.targetResolution.toUpperCase()}`}
                      </p>
                      {apiType !== "demo" && (
                        <div
                          onClick={() => {
                            setExpandAdvancedSettings(true);
                            setTimeout(() => {
                              settingsRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }, 100);
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            marginTop: 8,
                            padding: "4px 8px",
                            background: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: MediasfuColors.primary,
                          }}
                        >
                          <FontAwesomeIcon icon={faCog} /> Adjust Settings
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: MediasfuSpacing.md }}>
                      <label style={labelStyle}>
                        <FontAwesomeIcon icon={faUsers} /> People Per Page
                      </label>
                      <input
                        type="number"
                        value={itemPageLimit === 0 ? "" : itemPageLimit}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            setItemPageLimit(0);
                          } else {
                            const val = parseInt(raw, 10);
                            if (!isNaN(val)) {
                              setItemPageLimit(val);
                              if (val > 0) meetingParams.current.itemPageLimit = val;
                            }
                          }
                        }}
                        style={inputStyle}
                        title="Max tiles shown on the grid at once"
                      />
                      {renderTip("Max grid tiles - others paginate; also caps concurrent whiteboard users and breakout room size.")}
                    </div>

                    <div style={{ marginTop: MediasfuSpacing.md }}>
                      <label style={labelStyle}>
                        <FontAwesomeIcon icon={faDoorOpen} /> Waiting Room
                      </label>
                      {renderToggle(waitRoom, setWaitRoom, waitingRoomDisabled, "Waiting Room Toggle")}
                      {waitingRoomDisabled
                        ? renderTip(apiType === "demo" ? "Disabled in demo mode; upgrade to enable." : "Not available for chat events.")
                        : renderTip("Enable to approve each attendee before they enter.")}
                    </div>

                    {/* Safe Room - hidden for now (only show if date < 2024) */}
                    {new Date().getFullYear() < 2024 && (
                      <div style={{ marginTop: MediasfuSpacing.md }}>
                        <label style={labelStyle}>
                          <FontAwesomeIcon icon={faShieldAlt} /> Safe Room
                        </label>
                        {renderToggle(safeRoom, setSafeRoom, safeRoomDisabled, "Safe Room Toggle")}
                      </div>
                    )}

                    {!codeVisible && !eventScheduled.current && (
                      <div style={tipShellStyle(showTips)} aria-hidden={!showTips}>
                        <div style={tipInnerStyle}>
                        <div
                          style={{
                            padding: MediasfuSpacing.md,
                            background: isDarkMode ? "rgba(45,212,191,0.12)" : "rgba(45,212,191,0.08)",
                            borderRadius: MediasfuBorders.md,
                            border: `1px solid ${isDarkMode ? "rgba(45,212,191,0.28)" : "rgba(45,212,191,0.18)"}`,
                            marginTop: MediasfuSpacing.md,
                          }}
                        >
                          <p style={{ ...mutedTextStyle, margin: 0, display: "flex", alignItems: "center", gap: MediasfuSpacing.xs }}>
                            <FontAwesomeIcon icon={faInfoCircle} style={{ color: MediasfuColors.primary }} /> Provide a display name at minimum. Leave date empty to start now.
                          </p>
                        </div>
                        </div>
                      </div>
                    )}

                    {codeVisible && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: MediasfuSpacing.lg,
                          rowGap: MediasfuSpacing.md,
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                          width: "100%",
                          boxSizing: "border-box",
                          padding: MediasfuSpacing.lg,
                          background: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(45,212,191,0.07)",
                          borderRadius: MediasfuBorders.lg,
                          marginTop: MediasfuSpacing.md,
                        }}
                      >
                        {allowScan && (
                          <QRCodeSVG value={`${userName};${urll};${userSecret};pass;${meetingID}`} size={96} />
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, marginBottom: MediasfuSpacing.xs }}>Invite quickly</p>
                          <p style={{ margin: 0, fontSize: "0.9rem", wordBreak: "break-word" }}>
                            1. <button onClick={() => cleanupAndNavigate(() => { window.location.href = urll; })} style={{ color: MediasfuColors.primary, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Open link</button>
                          </p>
                          {allowScan && (
                            <p style={{ margin: 0, fontSize: "0.9rem" }}>2. Or scan QR code.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>

                <div style={dividerStyle}></div>

                {/* Advanced Settings Accordions */}
                <div style={{ marginBottom: MediasfuSpacing.md }} ref={settingsRef}>
                  
                  {/* Advanced Settings Header */}
                  <div
                    onClick={() => setExpandAdvancedSettings(!expandAdvancedSettings)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: `${MediasfuSpacing.sm}px ${MediasfuSpacing.md}px`,
                      background: isDarkMode ? "rgba(255,255,255,0.04)" : "#f8fafc",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                      borderRadius: MediasfuBorders.md,
                      cursor: "pointer",
                      marginBottom: MediasfuSpacing.sm,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.sm }}>
                      <FontAwesomeIcon icon={faCog} style={{ color: MediasfuColors.primary }} />
                      <span style={{ fontWeight: 600, fontSize: "0.9rem", color: isDarkMode ? "#e2e8f0" : "#334155" }}>
                        Advanced Settings
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={expandAdvancedSettings ? faChevronUp : faChevronDown}
                      style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                    />
                  </div>

                  {expandAdvancedSettings && (
                    <div style={{ paddingLeft: MediasfuSpacing.md }}>
                      <p style={{ fontSize: "0.85rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: MediasfuSpacing.md, fontStyle: "italic" }}>
                        Settings respect your saved defaults or account limits. Adjust from your dashboard or contact support for upgrades.
                      </p>
                      {/* Recording Parameters Accordion */}
                  {showRecording && (
                    <>
                      <div
                        onClick={() => setExpandRecordingParams(!expandRecordingParams)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: `${MediasfuSpacing.sm}px ${MediasfuSpacing.md}px`,
                          background: isDarkMode ? "rgba(255,255,255,0.04)" : "#f8fafc",
                          border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                          borderRadius: MediasfuBorders.md,
                          cursor: "pointer",
                          marginBottom: MediasfuSpacing.sm,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.sm }}>
                          <FontAwesomeIcon icon={faRecordVinyl} style={{ color: MediasfuColors.accent }} />
                          <span style={{ fontWeight: 600, fontSize: "0.9rem", color: isDarkMode ? "#e2e8f0" : "#334155" }}>
                            Recording Parameters
                          </span>
                        </div>
                        <FontAwesomeIcon
                          icon={expandRecordingParams ? faChevronUp : faChevronDown}
                          style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                        />
                      </div>

                      {expandRecordingParams && (
                        <div
                          style={{
                            marginBottom: MediasfuSpacing.lg,
                            padding: MediasfuSpacing.md,
                            background: isDarkMode ? "rgba(255,255,255,0.02)" : "#fafafa",
                            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}`,
                            borderRadius: MediasfuBorders.md,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                            gap: MediasfuSpacing.md,
                          }}
                        >
                          {/* Recording Fields */}
                          <div>
                            <label style={labelStyle}>Audio Pauses Limit</label>
                            <input type="number" defaultValue={recordingParams.current.recordingAudioPausesLimit} onChange={handleRecordingParamChange("recordingAudioPausesLimit", "number")} style={tooltipInputStyle} title="Max audio pauses allowed during recording" />
                            {renderTip("Max times audio recording can be paused.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Audio Support</label>
                            <select defaultValue={recordingParams.current.recordingAudioSupport as any} onChange={handleRecordingParamChange("recordingAudioSupport", "boolean")} style={selectStyle} title="Enable or disable audio recording">
                              <option value="true">Allowed</option>
                              <option value="false">Not Allowed</option>
                            </select>
                            {renderTip("Allow audio-only recording.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Audio People Limit</label>
                            <input type="number" defaultValue={recordingParams.current.recordingAudioPeopleLimit} onChange={handleRecordingParamChange("recordingAudioPeopleLimit", "number")} style={tooltipInputStyle} title="Max participants for audio recording" />
                            {renderTip("Max participants included in audio recording.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Video Pauses Limit</label>
                            <input type="number" defaultValue={recordingParams.current.recordingVideoPausesLimit} onChange={handleRecordingParamChange("recordingVideoPausesLimit", "number")} style={tooltipInputStyle} title="Max video pauses allowed during recording" />
                            {renderTip("Max times video recording can be paused.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Video Support</label>
                            <select defaultValue={recordingParams.current.recordingVideoSupport as any} onChange={handleRecordingParamChange("recordingVideoSupport", "boolean")} style={selectStyle} title="Enable or disable video recording">
                              <option value="true">Allowed</option>
                              <option value="false">Not Allowed</option>
                            </select>
                            {renderTip("Allow video recording.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Video People Limit</label>
                            <input type="number" defaultValue={recordingParams.current.recordingVideoPeopleLimit} onChange={handleRecordingParamChange("recordingVideoPeopleLimit", "number")} style={tooltipInputStyle} title="Max participants for video recording" />
                            {renderTip("Max participants included in video recording.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Preferred Orientation</label>
                            <select defaultValue={recordingParams.current.recordingPreferredOrientation} onChange={handleRecordingParamChange("recordingPreferredOrientation", "string")} style={selectStyle} title="Recording orientation preference">
                              <option value="landscape">Landscape</option>
                              <option value="portrait">Portrait</option>
                            </select>
                            {renderTip("Primary recomposition layout; individual videos stay in original format.")}
                          </div>
                          <div>
                            <label style={labelStyle}>Multi-Formats Support</label>
                            <select defaultValue={recordingParams.current.recordingMultiFormatsSupport as any} onChange={handleRecordingParamChange("recordingMultiFormatsSupport", "boolean")} style={selectStyle} title="Allow multiple recording formats">
                              <option value="true">Allowed</option>
                              <option value="false">Not Allowed</option>
                            </select>
                            {renderTip("Extra formats beyond the primary orientation (e.g., MP4 + HLS).")}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Meeting Parameters Accordion */}
                  <div
                    onClick={() => setExpandMeetingParams(!expandMeetingParams)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: `${MediasfuSpacing.sm}px ${MediasfuSpacing.md}px`,
                      background: isDarkMode ? "rgba(255,255,255,0.04)" : "#f8fafc",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                      borderRadius: MediasfuBorders.md,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.sm }}>
                      <FontAwesomeIcon icon={faCog} style={{ color: MediasfuColors.primary }} />
                      <span style={{ fontWeight: 600, fontSize: "0.9rem", color: isDarkMode ? "#e2e8f0" : "#334155" }}>
                        Meeting Parameters
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={expandMeetingParams ? faChevronUp : faChevronDown}
                      style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                    />
                  </div>

                  {expandMeetingParams && (
                    <div
                      style={{
                        marginTop: MediasfuSpacing.md,
                        padding: MediasfuSpacing.md,
                        background: isDarkMode ? "rgba(255,255,255,0.02)" : "#fafafa",
                        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}`,
                        borderRadius: MediasfuBorders.md,
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: MediasfuSpacing.md,
                      }}
                    >
                      {/* Meeting Fields */}
                      <div>
                        <label style={labelStyle}>Event Type</label>
                        <select
                          defaultValue={meetingParams.current.type}
                          onChange={(e) => {
                            handleMeetingParamChange("type", "string")(e as any);
                            setActType(e.target.value as MeetingParams["type"]);
                          }}
                          style={selectStyle}
                          title="Choose the type of event"
                        >
                          <option value="broadcast">Broadcast</option>
                          <option value="webinar">Webinar</option>
                          <option value="conference">Conference</option>
                          <option value="chat">Chat</option>
                        </select>
                        {renderTip(
                          actType === "broadcast"
                            ? "Highest capacity; only host produces; supports recording; no screenshare/whiteboard."
                            : actType === "webinar"
                            ? "Like conference but host stays on main stage; supports all features."
                            : actType === "conference"
                            ? "All participants equal; supports recording, screenshare, whiteboard, breakout rooms."
                            : "Private 1-on-1; max 2 people; no recording, screenshare, or whiteboard."
                        )}
                      </div>
                      <div>
                        <label style={labelStyle}>Media Type</label>
                        <select defaultValue={meetingParams.current.mediaType} onChange={handleMeetingParamChange("mediaType", "string")} style={selectStyle} title="Video or audio-only meeting">
                          <option value="video">Video</option>
                          <option value="audio">Audio</option>
                        </select>
                        {renderTip("Switch to audio-only for low bandwidth situations.")}
                      </div>
                      <div>
                        <label style={labelStyle}>People Per Page</label>
                        <input
                          type="number"
                          value={itemPageLimit === 0 ? "" : itemPageLimit}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              setItemPageLimit(0);
                            } else {
                              const val = parseInt(raw, 10);
                              if (!isNaN(val)) {
                                setItemPageLimit(val);
                                if (val > 0) meetingParams.current.itemPageLimit = val;
                              }
                            }
                          }}
                          style={tooltipInputStyle}
                          title="Max participants visible per page"
                        />
                        {renderTip("Max grid tiles; also caps concurrent whiteboard users and breakout room size.")}
                      </div>
                      <div>
                        <label style={labelStyle}>Video Resolution (Others)</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "QHD", value: "qhd" },
                            { label: "FHD", value: "fhd" },
                            { label: "HD", value: "hd" },
                            { label: "SD", value: "sd" },
                            { label: "QnHD", value: "qnhd" },
                          ]}
                          value={meetingParams.current.targetResolution}
                          onChange={(val) => handleMeetingParamChange("targetResolution", "string")({ target: { value: val } } as any)}
                        />
                        {renderTip("Target max quality for participants (QnHD is lowest, QHD is highest).")}
                      </div>
                      <div>
                        <label style={labelStyle}>Video Resolution (Host)</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "QHD", value: "qhd" },
                            { label: "FHD", value: "fhd" },
                            { label: "HD", value: "hd" },
                            { label: "SD", value: "sd" },
                            { label: "QnHD", value: "qnhd" },
                          ]}
                          value={meetingParams.current.targetResolutionHost}
                          onChange={(val) => handleMeetingParamChange("targetResolutionHost", "string")({ target: { value: val } } as any)}
                        />
                        {renderTip("Target max quality for the host (QnHD is lowest, QHD is highest).")}
                      </div>
                      <div>
                        <label style={labelStyle}>SIP Support</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "No", value: "false", icon: faTimes },
                            { label: "Yes", value: "true", icon: faCheck },
                          ]}
                          value={meetingParams.current.supportSIP ? "true" : "false"}
                          onChange={(val) => handleMeetingParamChange("supportSIP", "boolean")({ target: { value: val } } as any)}
                        />
                        {renderTip("Enable SIP support for this meeting.")}
                      </div>
                      {meetingParams.current.supportSIP && (
                        <div>
                          <label style={labelStyle}>SIP Direction</label>
                          <RenderSegmentedControl
                            options={[
                              { label: "Inbound", value: "inbound" },
                              { label: "Outbound", value: "outbound" },
                              { label: "Both", value: "both" },
                            ]}
                            value={meetingParams.current.directionSIP}
                            onChange={(val) => handleMeetingParamChange("directionSIP", "string")({ target: { value: val } } as any)}
                          />
                          {renderTip("Direction of SIP calls allowed.")}
                        </div>
                      )}
                      {/* Translation Support - only show toggle if enabled per account (refMeetingParams) */}
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <label style={labelStyle}>Translation (Multi-Language)</label>
                          {!(refMeetingParams.current.supportTranslation && refMeetingParams.current.translationConfigNickName) && (
                            <FontAwesomeIcon icon={faLock} style={{ fontSize: 12, color: "#9ca3af" }} />
                          )}
                        </div>
                        <RenderSegmentedControl
                          options={[
                            { label: "No", value: "false", icon: faTimes },
                            { label: "Yes", value: "true", icon: faCheck },
                          ]}
                          value={meetingParams.current.supportTranslation ? "true" : "false"}
                          onChange={(refMeetingParams.current.supportTranslation && refMeetingParams.current.translationConfigNickName)
                            ? (val) => handleMeetingParamChange("supportTranslation", "boolean")({ target: { value: val } } as any)
                            : undefined}
                          disabled={!(refMeetingParams.current.supportTranslation && refMeetingParams.current.translationConfigNickName)}
                        />
                        {renderTip(
                          (refMeetingParams.current.supportTranslation && refMeetingParams.current.translationConfigNickName)
                            ? "Enable multi-language translation for this meeting."
                            : "Translation is not enabled for your account. Enable it from your dashboard or contact support."
                        )}
                      </div>
                      <div>
                        <label style={labelStyle}>Audio Setting</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "Approval", value: "approval", icon: faQuestionCircle },
                            { label: "Disallow", value: "disallow", icon: faTimes },
                            { label: "Allow", value: "allow", icon: faCheck },
                          ]}
                          value={meetingParams.current.audioSetting}
                          onChange={(val) => handleMeetingParamChange("audioSetting", "string")({ target: { value: val } } as any)}
                        />
                        {renderTip("Controls if others can use their mics; you can adjust this during the session.")}
                      </div>
                      <div>
                        <label style={labelStyle}>Server Noise Suppression</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "Account default", value: "default" },
                            { label: "On", value: "true", icon: faCheck },
                            { label: "Off", value: "false", icon: faTimes },
                          ]}
                          value={meetingParams.current.backendAudioDenoise?.enabled === true
                            ? "true"
                            : meetingParams.current.backendAudioDenoise?.enabled === false
                              ? "false"
                              : "default"}
                          onChange={(value) => {
                            if (value === "default") delete meetingParams.current.backendAudioDenoise;
                            else meetingParams.current.backendAudioDenoise = value === "true"
                              ? { enabled: true, profile: "arnndn" }
                              : { enabled: false };
                            forceRender();
                          }}
                        />
                        {renderTip("Reduces steady background noise on the server. Requires an eligible account; Account default leaves this room setting unchanged. Browser echo cancellation remains separate.")}
                      </div>
                      <div>
                        <label style={labelStyle}>Video Setting</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "Approval", value: "approval", icon: faQuestionCircle },
                            { label: "Disallow", value: "disallow", icon: faTimes },
                            { label: "Allow", value: "allow", icon: faCheck },
                          ]}
                          value={meetingParams.current.videoSetting}
                          onChange={(val) => handleMeetingParamChange("videoSetting", "string")({ target: { value: val } } as any)}
                        />
                        {renderTip("Controls if others can turn cameras on; adjustable mid-session.")}
                      </div>
                      <div>
                        <label style={labelStyle}>Screenshare Setting</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "Approval", value: "approval", icon: faQuestionCircle },
                            { label: "Disallow", value: "disallow", icon: faTimes },
                            { label: "Allow", value: "allow", icon: faCheck },
                          ]}
                          value={meetingParams.current.screenshareSetting}
                          onChange={(val) => handleMeetingParamChange("screenshareSetting", "string")({ target: { value: val } } as any)}
                        />
                        {renderTip("Controls if others can share screens; change anytime during the call.")}
                      </div>
                      <div>
                        <label style={labelStyle}>Chat Setting</label>
                        <RenderSegmentedControl
                          options={[
                            { label: "Disallow", value: "disallow", icon: faTimes },
                            { label: "Allow", value: "allow", icon: faCheck },
                          ]}
                          value={meetingParams.current.chatSetting}
                          onChange={(val) => handleMeetingParamChange("chatSetting", "string")({ target: { value: val } } as any)}
                        />
                        {renderTip("Toggle chat availability for participants; you can update this in-session.")}
                      </div>
                    </div>
                  )}
                    </div>
                  )}
                </div>

                {!codeVisible && !eventScheduled.current && (
                  <div className="mms-actionbar" style={{ display: "flex", gap: MediasfuSpacing.md, justifyContent: "flex-end", marginTop: MediasfuSpacing.md }}>
                    <PremiumButton
                      variant="outlined" borderColor={isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.14)"} textColor={brand.soft}
                      onPress={() => {
                        setActivePanel("join");
                        setShowStartMeetingPanel(false);
                      }}
                      icon={<FontAwesomeIcon icon={faTimes} />}
                      isDarkMode={isDarkMode}
                    >
                      Cancel
                    </PremiumButton>
                    <PremiumButton
                      variant="gradient" gradient={MEDIASFU_BRAND.gradient} textColor={MEDIASFU_BRAND.ink}
                      onPress={async () => await handleStartMeetingClick()}
                      icon={<FontAwesomeIcon icon={faCheck} />}
                      isDarkMode={isDarkMode}
                    >
                      Create meeting
                    </PremiumButton>
                  </div>
                )}
              </div>
            )}
            </div>
          </section>

          {/* Stage: the live preview is the hero. */}
          <section className="mms-stage" aria-label="Camera and microphone check">
            <div className="mms-stage__frame">
              {initMedia ? (
                <>
                  <video
                    id="previewVideoRight"
                    ref={videoOutputPreviewRef}
                    muted
                    autoPlay
                    playsInline
                    className="mms-stage__video"
                  />
                  <span className="mms-live-ring" aria-hidden="true" />
                  {isModelLoading && (
                    <div className="mms-stage__loading">
                      <div className="loader-spin" style={{ position: "relative", width: 36, height: 36 }} />
                    </div>
                  )}
                </>
              ) : (
                <button type="button" className="mms-stage__enable" onClick={() => setInitMedia(true)}>
                  <OrbitPlaceholder
                    initial={userName.trim() ? userName.trim()[0].toUpperCase() : "Hi"}
                    isDarkMode={isDarkMode}
                  />
                  <span className="mms-stage__cta">
                    <FontAwesomeIcon icon={faVideo} /> Turn on camera
                  </span>
                </button>
              )}
              <div className="mms-stage__top">
                <span className="mms-glass-pill">Green room</span>
                <span className={`mms-glass-pill mms-status${initMedia ? " is-live" : ""}`}>
                  <span className="mms-status__dot" aria-hidden="true" />
                  {initMedia ? "Preview on" : "Camera off"}
                </span>
              </div>
              <div className="mms-stage__bottom">
                <span className="mms-glass-pill mms-nametag">{userName.trim() || "You"}</span>
                <span className="mms-glass-pill mms-mic" title="Microphone level">
                  <FontAwesomeIcon icon={faMicrophone} />
                  <MicLevelMeter streamRef={localStreamVideo} active={initMedia} />
                </span>
              </div>
            </div>
            <p className="mms-stage__hint">
              {initMedia
                ? "Say something: the bars should move. Pick another camera or mic below."
                : "Nothing is shared until you join. Your preview stays on this device."}
            </p>

            {/* Compact Device Selectors - Hidden when panel expanded */}
            {!showMediaSettingsPanel && (
              <div style={compactSelectorGridStyle}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...labelStyle, fontSize: "0.75rem", marginBottom: 4 }}>
                    <FontAwesomeIcon icon={faVideo} style={{ marginRight: 4 }} /> Camera
                  </label>
                  <div style={{ position: "relative" }}>
                    <select
                      value={selectedVideoDevice}
                      onChange={(e) => handleCameraChange(e.target.value)}
                      aria-label="Camera device"
                      style={{
                        ...selectStyle,
                        height: 40,
                        fontSize: "0.8rem",
                        paddingLeft: MediasfuSpacing.sm,
                        paddingRight: 32,
                      }}
                    >
                      <option value="">Default</option>
                      {cameraDevices.map((d, i) => (
                        <option key={d.deviceId || i} value={d.deviceId}>
                          {d.label || `Camera ${i + 1}`}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: "0.7rem", color: isDarkMode ? "#94a3b8" : "#64748b" }} />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...labelStyle, fontSize: "0.75rem", marginBottom: 4 }}>
                    <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: 4 }} /> Mic
                  </label>
                  <div style={{ position: "relative" }}>
                    <select
                      value={selectedAudioDevice}
                      onChange={(e) => handleMicrophoneChange(e.target.value)}
                      aria-label="Microphone device"
                      style={{
                        ...selectStyle,
                        height: 40,
                        fontSize: "0.8rem",
                        paddingLeft: MediasfuSpacing.sm,
                        paddingRight: 32,
                      }}
                    >
                      <option value="">Default</option>
                      {microphoneDevices.map((d, i) => (
                        <option key={d.deviceId || i} value={d.deviceId}>
                          {d.label || `Mic ${i + 1}`}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: "0.7rem", color: isDarkMode ? "#94a3b8" : "#64748b" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Expanded Media Settings */}
            {showMediaSettingsPanel && (
              <div style={{ display: "flex", flexDirection: "column", gap: MediasfuSpacing.sm }}>
                {/* Camera & Video Section */}
                <div
                  style={{
                    background: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.03)",
                    borderRadius: MediasfuBorders.md,
                    padding: MediasfuSpacing.md,
                    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                  }}
                >
                  <button
                    onClick={() => setShowCameraSection((v) => !v)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: `${MediasfuSpacing.xs}px 0`,
                      color: isDarkMode ? "#e2e8f0" : "#334155",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faVideo} style={{ marginRight: 8, color: MediasfuColors.primary }} />
                      Camera & Background
                    </span>
                    <FontAwesomeIcon
                      icon={showCameraSection ? faChevronUp : faChevronDown}
                      style={{ fontSize: "0.75rem", opacity: 0.7 }}
                    />
                  </button>
                  {showCameraSection && (
                    <div style={{ paddingTop: MediasfuSpacing.xs }}>
                      <label style={{ ...labelStyle, fontSize: "0.8rem" }}>Camera Device</label>
                      <div style={{ position: "relative", marginBottom: MediasfuSpacing.sm }}>
                        <select
                          value={selectedVideoDevice}
                          onChange={(e) => handleCameraChange(e.target.value)}
                          style={selectStyle}
                        >
                          <option value="">Default Camera</option>
                          {cameraDevices.map((d, i) => (
                            <option key={d.deviceId || i} value={d.deviceId}>
                              {d.label || `Camera ${i + 1}`}
                            </option>
                          ))}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: "0.7rem", color: isDarkMode ? "#94a3b8" : "#64748b" }} />
                      </div>

                      <VirtualBackgroundSelector
                        virtualBackground={virtualBackground}
                        onSelect={handleVirtualBackgroundSelect}
                        onUpload={handleImageUpload}
                        isDarkMode={isDarkMode}
                        isLoading={isModelLoading}
                      />
                      <div style={{ 
                        fontSize: "0.65rem", 
                        color: isDarkMode ? "#94a3b8" : "#64748b", 
                        marginTop: 6, 
                        fontStyle: "italic",
                        textAlign: "right"
                      }}>
                        Black screen? Change the selected image to update/clear.
                      </div>
                    </div>
                  )}
                </div>

                {/* Audio Devices Section */}
                <div
                  style={{
                    background: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.03)",
                    borderRadius: MediasfuBorders.md,
                    padding: MediasfuSpacing.md,
                    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                  }}
                >
                  <button
                    onClick={() => setShowAudioSection((v) => !v)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: `${MediasfuSpacing.xs}px 0`,
                      color: isDarkMode ? "#e2e8f0" : "#334155",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: 8, color: MediasfuColors.primary }} />
                      Audio Devices
                    </span>
                    <FontAwesomeIcon
                      icon={showAudioSection ? faChevronUp : faChevronDown}
                      style={{ fontSize: "0.75rem", opacity: 0.7 }}
                    />
                  </button>
                  {showAudioSection && (
                    <div style={{ paddingTop: MediasfuSpacing.xs }}>
                      <label style={{ ...labelStyle, fontSize: "0.8rem" }}>
                        <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: 6 }} />
                        Microphone
                      </label>
                      <div style={{ position: "relative", marginBottom: MediasfuSpacing.sm }}>
                        <select
                          value={selectedAudioDevice}
                          onChange={(e) => handleMicrophoneChange(e.target.value)}
                          style={selectStyle}
                        >
                          <option value="">Default Microphone</option>
                          {microphoneDevices.map((d, i) => (
                            <option key={d.deviceId || i} value={d.deviceId}>
                              {d.label || `Microphone ${i + 1}`}
                            </option>
                          ))}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: "0.7rem", color: isDarkMode ? "#94a3b8" : "#64748b" }} />
                      </div>

                      <label style={{ ...labelStyle, fontSize: "0.8rem" }}>
                        <FontAwesomeIcon icon={faVolumeUp} style={{ marginRight: 6 }} />
                        Speaker / Output
                      </label>
                      <div style={{ position: "relative", marginBottom: MediasfuSpacing.xs }}>
                        <select
                          value={audioOutputPreference.current || ""}
                          onChange={(e) => handleAudioOutputChange(e.target.value)}
                          style={selectStyle}
                        >
                          <option value="">Default Speaker</option>
                          {speakerDevices.map((d, i) => (
                            <option key={d.deviceId || i} value={d.deviceId}>
                              {d.label || `Speaker ${i + 1}`}
                            </option>
                          ))}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: "0.7rem", color: isDarkMode ? "#94a3b8" : "#64748b" }} />
                      </div>

                      <PremiumButton
                        variant="glass"
                        onPress={handleTestAudio}
                        icon={<FontAwesomeIcon icon={audioTestState === "idle" ? faPlayCircle : audioTestState === "recording" ? faMicrophone : faVolumeUp} spin={audioTestState === "recording"} />}
                        isDarkMode={isDarkMode}
                        style={{ marginTop: MediasfuSpacing.xs, width: "100%" }}
                        disabled={audioTestState !== "idle"}
                      >
                        {audioTestState === "idle" ? "Test Audio Output" : audioTestState === "recording" ? "Recording... Speak now" : "Playing back..."}
                      </PremiumButton>
                      <audio ref={audioPreviewRef} style={{ display: "none" }} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Toggle Button */}
            <PremiumButton
              variant="glass"
              onPress={() => {
                setShowMediaSettingsPanel((v) => !v);
                setShowMediaSettings((v) => !v);
                if (!initMedia) setInitMedia(true);
              }}
              icon={<FontAwesomeIcon icon={showMediaSettingsPanel ? faChevronUp : faCog} />}
              isDarkMode={isDarkMode}
              style={{ marginTop: MediasfuSpacing.sm }}
            >
              {showMediaSettingsPanel ? "Hide settings" : "More settings"}
            </PremiumButton>
          </section>
          {/* END STAGE */}
        </div>

        {showDownloadPromo && (
          <section
            className="mms-appstrip"
            aria-labelledby="mediasfu-download-promo-title"
            style={{
              width: "100%",
              maxWidth: cardContainerMaxWidth,
              margin: `${MediasfuSpacing.md}px auto 0`,
              padding: `${MediasfuSpacing.md}px ${MediasfuSpacing.md + 4}px`,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              gap: MediasfuSpacing.md,
              flexWrap: "wrap",
              borderRadius: 16,
              background: isDarkMode ? "rgba(11,18,32,0.6)" : "rgba(255,255,255,0.7)",
              border: `1px solid ${brand.line}`,
              boxShadow: "none",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 42,
                height: 42,
                flex: "0 0 42px",
                display: "grid",
                placeItems: "center",
                borderRadius: 12,
                color: isDarkMode ? MEDIASFU_BRAND.skyLight : "#0369A1",
                background: "rgba(56,189,248,0.10)",
                border: "1px solid rgba(56,189,248,0.24)",
              }}
            >
              <FontAwesomeIcon icon={faDownload} />
            </div>

            <div style={{ flex: "1 1 330px", minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  marginBottom: 3,
                  color: brand.muted,
                  fontSize: "0.66rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                MediaSFU apps
              </span>
              <h2
                id="mediasfu-download-promo-title"
                style={{
                  margin: 0,
                  color: brand.text,
                  fontFamily: displayFont,
                  fontSize: "clamp(0.98rem, 1.5vw, 1.08rem)",
                  fontWeight: 600,
                  letterSpacing: "-0.015em",
                }}
              >
                Prefer the app? Take MediaSFU with you.
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  color: brand.muted,
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                }}
              >
                Install MediaSFU for desktop or mobile to keep meetings, calling, AI assistance, and live translation close at hand.
              </p>
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: MediasfuSpacing.sm,
                flexWrap: "wrap",
              }}
            >
              <a
                href={downloadPromoHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  minHeight: 38,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  padding: "8px 14px",
                  borderRadius: 10,
                  color: brand.text,
                  background: brand.fill,
                  border: `1px solid ${brand.lineStrong}`,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                <FontAwesomeIcon icon={downloadPlatformIcon} /> {downloadPlatform.label}
              </a>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <a
                  href={allDownloadsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    color: accentText,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  All platforms <FontAwesomeIcon icon={faArrowRight} />
                </a>
                {/* Reassurance that this is not a single-platform product. The
                    detected platform is left out — the CTA above already is it. */}
                <div style={{ display: "flex", alignItems: "center", gap: MediasfuSpacing.sm + 2 }}>
                  {otherPlatforms.map((platform) => (
                    <a
                      key={platform.label}
                      href={allDownloadsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={platform.label}
                      aria-label={`MediaSFU for ${platform.label}`}
                      style={{
                        color: isDarkMode ? "#8fa3bd" : "#64748b",
                        fontSize: "0.85rem",
                        lineHeight: 1,
                        textDecoration: "none",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? "#e2e8f0" : "#0f172a"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? "#8fa3bd" : "#64748b"; }}
                    >
                      <FontAwesomeIcon icon={platform.icon} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      {waitingModalVisible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            style={{
              background: brand.popover,
              padding: MediasfuSpacing.xl,
              borderRadius: 18,
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 32px 64px -24px rgba(0, 0, 0, 0.7)",
              border: `1px solid ${brand.line}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                border: `3px solid ${MediasfuColors.primary}`,
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginBottom: MediasfuSpacing.lg,
              }}
            />
            <style>
              {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
            </style>
            <h6 style={{ ...sectionTitleStyle, marginBottom: MediasfuSpacing.sm }}>
              Waiting room
            </h6>
            <p style={{ ...mutedTextStyle, margin: 0 }}>
              Waiting for host to join or allow you in. You will be automatically redirected.
            </p>
            <div style={{ marginTop: MediasfuSpacing.lg }}>
              <PremiumButton
                variant="outlined" borderColor={isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.14)"} textColor={brand.soft}
                onPress={() => setWaitingModalVisible(false)}
                isDarkMode={isDarkMode}
              >
                Leave
              </PremiumButton>
            </div>
          </div>
        </div>
      )}

      {adminWelcomeModalVisible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            style={{
              background: isDarkMode ? "rgba(30,41,59,0.95)" : "rgba(255,255,255,0.98)",
              padding: MediasfuSpacing.xl,
              borderRadius: MediasfuBorders.xl,
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            }}
          >
            <h5 style={{ ...sectionTitleStyle, marginBottom: MediasfuSpacing.md }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ color: MediasfuColors.primary }} /> Welcome back, host
            </h5>
            <p style={{ ...mutedTextStyle, marginBottom: MediasfuSpacing.md }}>
              Enter the passcode to continue:
            </p>
            <div style={{ display: "flex", gap: MediasfuSpacing.sm, alignItems: "center" }}>
              <input
                type={showPasscode ? "text" : "password"}
                className="form-control"
                id="passcodeInput"
                placeholder="Enter passcode"
                value={passcodeInput}
                maxLength={64}
                onChange={(e) => setPasscodeInput(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => setShowPasscode(!showPasscode)}
                title={showPasscode ? "Hide Passcode" : "Show Passcode"}
                style={{
                  background: "transparent",
                  border: "none",
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  cursor: "pointer",
                  padding: MediasfuSpacing.sm,
                }}
              >
                <FontAwesomeIcon icon={showPasscode ? faEyeSlash : faEye} />
              </button>
            </div>
            {warningMessage && (
              <div style={{ color: "#ef4444", marginTop: MediasfuSpacing.sm, fontSize: "0.9rem" }}>
                {warningMessage}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: MediasfuSpacing.lg }}>
              <PremiumButton
                variant="gradient" gradient={MEDIASFU_BRAND.gradient} textColor={MEDIASFU_BRAND.ink}
                onPress={handleSubmitPasscode}
                icon={<FontAwesomeIcon icon={faCheck} />}
                isDarkMode={isDarkMode}
              >
                Submit
              </PremiumButton>
            </div>
          </div>
        </div>
      )}

      {/* Off-screen helpers kept at zero size to avoid layout shifts when unused */}
      <canvas
        id="mainCanvas"
        ref={mainCanvasRef}
        style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
      ></canvas>
      <canvas
        id="backgroundCanvas"
        ref={backgroundCanvasRef}
        style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
      ></canvas>
      <video
        id="captureVideo"
        muted
        ref={captureVideoRef}
        autoPlay
        playsInline
        style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
      ></video>
    </div>
    </>
  );
};

export default ModernMeetingSetup;
